// ============ PAGES: Dashboard / Scanner / IPO / Earnings / Dividends ============
import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  ASSETS, INDICES, SUMMARY, NEWS, IPO_LIST, DIVIDENDS_LIST,
  genCandles, fmtNum, fmtBig, mmRng,
} from '../data';
import { SecHead, Sparkline, MemberGate, SymLogo, CardBoundary } from '../components/core';
import { BubbleWatchCard } from '../components/bubblewatch';
import { ParlovGapBlock } from '../components/parlovgap';
import { ParlovDailyTable } from '../components/parlovdaily';
import { SuperTdCard } from '../components/supertd';
import { SuperTdStats } from '../components/supertdstats';
import { MarketLearningCard } from '../components/marketlearning';
import { QuantWatchlistCard } from '../components/quantwatchlist';
import { GuruTradesCard } from '../components/gurutrades';
import { FeaturedGroups } from '../components/featured';
import { mmIsPremium } from '../lib/premium';
import { MM_LIVE, mt5OwnerBroker } from '../live';
import { useNews, useEarnings, useIpo, useDividends, useEconWeek, useOhlc, useMt5Ohlc, useFundamentals, useLiveTick, useClock } from '../hooks/useLiveData';
import { ago } from '../lib/ago';
import { fetchCryptoTotal } from '../services/coingecko';
import { indexYahoo, divYahoo, assetYahoo } from '../lib/yahoo';
import { useT, useLang } from '../lib/i18n';

// English labels for the summary mini-cards (keyed by sym; falls back to Thai).
const SUMMARY_EN: Record<string, string> = {
  TOTAL: 'Crypto market cap', USDTHB: 'USD to THB', XAUUSD: 'Gold spot', TH10Y: 'Thai 10Y yield',
  WTI: 'Crude oil WTI', BTC: 'Bitcoin',
};
const CLASS_TH: Record<string, string> = { Stocks: 'หุ้น', Crypto: 'คริปโต', Commodities: 'สินค้าโภคภัณฑ์', ETF: 'กองทุน ETF', Bonds: 'ตราสารหนี้' };

// Scanner uses module-level Thai labels (presets, columns, filters, ratings).
// This maps every Thai label → English; `tl(s)` in the component returns the
// English when in EN mode, else the original Thai (fallback keeps unknowns).
const SCAN_EN: Record<string, string> = {
  // presets
  'ทั้งหมด': 'All', 'หุ้นคุณค่า · P/E < 15': 'Value · P/E < 15', 'เติบโตสูง · Score 80+': 'High growth · Score 80+',
  'ปันผลเด่น · Yield > 3%': 'Top dividend · Yield > 3%', 'โมเมนตัมบวก': 'Positive momentum', 'ย่อตัวแรง': 'Big pullback',
  // class tab
  'ทุก Asset': 'All assets',
  // column sets
  'ภาพรวม': 'Overview', 'ประสิทธิภาพ': 'Performance', 'ทางเทคนิค': 'Technical', 'การประเมินมูลค่า': 'Valuation', 'เงินปันผล': 'Dividends',
  // columns
  'ราคา': 'Price', 'เปลี่ยน %': 'Chg %', 'ปริมาณ': 'Volume', 'ปริมาณสัมพัทธ์': 'Rel volume', 'มูลค่าตลาด': 'Mkt cap',
  'เติบโต EPS YoY': 'EPS growth YoY', 'หมวดธุรกิจ': 'Sector', 'คะแนนนักวิเคราะห์': 'Analyst rating',
  'วันนี้': 'Today', '1 สัปดาห์': '1W', '1 เดือน': '1M', '3 เดือน': '3M', '6 เดือน': '6M', '1 ปี': '1Y', '30 วัน': '30D',
  'แนวโน้ม': 'Trend', 'สัญญาณรวม': 'Signal', 'ปันผล/หุ้น': 'DPS', 'อันดับ Yield': 'Yield rank',
  // filter group labels
  'เปลี่ยนแปลง %': 'Change %', 'มูลค่าตามราคาตลาด': 'Market cap', 'การเติบโต EPS': 'EPS growth', 'เบต้า': 'Beta',
  // filter options
  'หุ้นไทย (SET)': 'Thai (SET)', 'หุ้นสหรัฐ (US)': 'US stocks', 'ญี่ปุ่น (JP)': 'Japan (JP)', 'ฮ่องกง/จีน (HK)': 'HK/China',
  'ยุโรป (EU)': 'Europe (EU)', 'อินเดีย (IN)': 'India (IN)', 'เกาหลี (KR)': 'Korea (KR)',
  'ต่ำกว่า 50': 'Under 50', '50 – 200': '50 – 200', '200 – 1,000': '200 – 1,000', 'มากกว่า 1,000': 'Over 1,000',
  'บวก (ขึ้น)': 'Up (+)', 'ลบ (ลง)': 'Down (−)', 'ขึ้นแรง > +2%': 'Strong up > +2%', 'ลงแรง < -2%': 'Strong down < -2%',
  'ขนาดใหญ่ (Mega)': 'Mega cap', 'ขนาดกลาง': 'Mid cap', 'ขนาดเล็ก': 'Small cap',
  'ถูก · ต่ำกว่า 15': 'Cheap · under 15', 'ปานกลาง · 15–30': 'Mid · 15–30', 'สูง · มากกว่า 30': 'High · over 30',
  'เติบโตบวก': 'Positive growth', 'เติบโตสูง > 20%': 'High growth > 20%', 'หดตัว (ติดลบ)': 'Shrinking (neg.)',
  'มีปันผล': 'Pays dividend', 'มากกว่า 3%': 'Over 3%', 'มากกว่า 5%': 'Over 5%',
  '1 เดือนบวก': '1M positive', '1 เดือน > 10%': '1M > 10%', 'YTD บวก': 'YTD positive',
  'ต่ำกว่า 1 (น่าสนใจ)': 'Under 1 (attractive)', 'ต่ำกว่า 2': 'Under 2',
  'ผันผวนต่ำ < 1': 'Low vol < 1', 'ผันผวนปานกลาง 1–1.5': 'Mid vol 1–1.5', 'ผันผวนสูง > 1.5': 'High vol > 1.5',
  // ratings + trends
  'มีแรงซื้อรุนแรง': 'Strong buy', 'มีแรงซื้อ': 'Buy', 'เป็นกลาง': 'Neutral', 'มีแรงขาย': 'Sell',
  'ขาขึ้นแข็งแกร่ง': 'Strong uptrend', 'ขาขึ้น': 'Uptrend', 'ขาลง': 'Downtrend', 'พักตัว': 'Consolidating',
  'ซื้อรุนแรง': 'Strong buy', 'ซื้อ': 'Buy', 'ถือ': 'Hold', 'ขาย': 'Sell',
};
// Module-level flag so the COLSETS render functions (defined outside any
// component) can localize too. ScannerPage sets it at the top of each render.
let _scanEn = false;
const TL = (s: string) => (_scanEn ? (SCAN_EN[s] || s) : s);

const sparkOf = (a: any) => genCandles(a.seed, a.price, 30, a.vol).map(c => c.c);

// ---- Intraday area chart with crosshair (market overview hero) ----
function AreaChart({ candles, labels, height = 300, up, decimals = 2, type = 'line' }: any) {
  const ref = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<any>(null);
  useEffect(() => {
    const cv = ref.current!, ctx = cv.getContext('2d')! as any;
    const DPR = Math.min(2, window.devicePixelRatio || 1);
    const W = cv.clientWidth, H = cv.clientHeight;
    cv.width = W * DPR; cv.height = H * DPR;
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    ctx.clearRect(0, 0, W, H);
    const padT = 12, padB = 24, padR = 64;
    // Same guard as CandleChart in analysis.tsx, for the same reason: an
    // unknown ticker or an upstream 404 hands this an empty array, the spread
    // into Math.min/max returns Infinity, and every coordinate below turns into
    // NaN. Canvas drops non-finite draws silently, so the failure looked like a
    // chart that simply never appeared. A single bar is guarded too - the x
    // scale divides by (n - 1), which is 0/0 at one point.
    if (!candles?.length) {
      ctx.fillStyle = 'rgba(255,255,255,0.38)';
      ctx.font = '12px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('ไม่มีข้อมูลราคา · No price data', W / 2, H / 2);
      return;
    }
    const data = candles.map((c: any) => c.c);
    const n = data.length;
    const min = type === 'line' ? Math.min(...data) : Math.min(...candles.map((c: any) => c.l));
    const max = type === 'line' ? Math.max(...data) : Math.max(...candles.map((c: any) => c.h));
    const rng = (max - min) || 1;
    const x = (i: number) => (n > 1 ? (i / (n - 1)) * (W - padR) : (W - padR) / 2);
    const y = (v: number) => padT + (H - padT - padB) * (1 - (v - min) / rng);
    const col = up ? '#00e0b0' : '#ff3b5c';
    ctx.font = '10px "IBM Plex Mono", monospace';
    for (let g = 0; g <= 4; g++) {
      const v = min + rng * g / 4, gy = y(v);
      ctx.strokeStyle = 'rgba(255,255,255,0.05)';
      ctx.beginPath(); ctx.moveTo(0, gy); ctx.lineTo(W - padR, gy); ctx.stroke();
      ctx.fillStyle = 'rgba(255,255,255,0.38)';
      ctx.fillText(fmtNum(v, v > 1000 ? 0 : decimals), W - padR + 8, gy + 3);
    }
    ctx.fillStyle = 'rgba(255,255,255,0.3)';
    for (let g = 0; g <= 6; g++) {
      const i = Math.round((n - 1) * g / 6);
      ctx.fillText(labels[i], Math.max(2, Math.min(x(i) - 14, W - padR - 36)), H - 8);
    }
    if (type === 'line') {
      const grad = ctx.createLinearGradient(0, padT, 0, H - padB);
      grad.addColorStop(0, up ? 'rgba(0,224,176,0.30)' : 'rgba(255,59,92,0.30)');
      grad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.beginPath();
      data.forEach((v: number, i: number) => i === 0 ? ctx.moveTo(x(i), y(v)) : ctx.lineTo(x(i), y(v)));
      ctx.lineTo(x(n - 1), H - padB); ctx.lineTo(0, H - padB); ctx.closePath();
      ctx.fillStyle = grad; ctx.fill();
      ctx.beginPath();
      data.forEach((v: number, i: number) => i === 0 ? ctx.moveTo(x(i), y(v)) : ctx.lineTo(x(i), y(v)));
      ctx.strokeStyle = col; ctx.lineWidth = 1.8;
      ctx.shadowColor = col; ctx.shadowBlur = 9;
      ctx.stroke(); ctx.shadowBlur = 0;
      ctx.beginPath(); ctx.arc(x(n - 1), y(data[n - 1]), 3.4, 0, Math.PI * 2);
      ctx.fillStyle = col; ctx.fill();
    } else {
      const cw = Math.max(2.5, ((W - padR) / n) * (type === 'candle' ? 0.62 : 0.55));
      candles.forEach((c: any, i: number) => {
        const colC = c.c >= c.o ? '#00e0b0' : '#ff3b5c';
        ctx.strokeStyle = colC; ctx.fillStyle = colC;
        ctx.lineWidth = type === 'bar' ? 1.6 : 1;
        ctx.beginPath(); ctx.moveTo(x(i), y(c.h)); ctx.lineTo(x(i), y(c.l)); ctx.stroke();
        if (type === 'candle') {
          const top = y(Math.max(c.o, c.c)), bh = Math.max(1, Math.abs(y(c.o) - y(c.c)));
          ctx.fillRect(x(i) - cw / 2, top, cw, bh);
        } else {
          ctx.beginPath(); ctx.moveTo(x(i) - cw / 2, y(c.o)); ctx.lineTo(x(i), y(c.o)); ctx.stroke();
          ctx.beginPath(); ctx.moveTo(x(i), y(c.c)); ctx.lineTo(x(i) + cw / 2, y(c.c)); ctx.stroke();
        }
      });
    }
    if (hover) {
      const i = Math.min(n - 1, Math.max(0, Math.round(hover.px / ((W - padR) / (n - 1)))));
      ctx.strokeStyle = 'rgba(255,255,255,0.28)'; ctx.setLineDash([3, 3]);
      ctx.beginPath(); ctx.moveTo(x(i), padT); ctx.lineTo(x(i), H - padB); ctx.stroke();
      ctx.setLineDash([]);
      ctx.beginPath(); ctx.arc(x(i), y(data[i]), 4.5, 0, Math.PI * 2);
      ctx.fillStyle = '#fff'; ctx.strokeStyle = col; ctx.lineWidth = 2; ctx.fill(); ctx.stroke();
      const bx = Math.min(x(i) + 12, W - padR - 124);
      ctx.fillStyle = 'rgba(10,17,26,0.94)'; ctx.strokeStyle = 'rgba(139,92,246,0.4)';
      ctx.beginPath(); ctx.roundRect(bx, padT + 4, 116, type === 'line' ? 46 : 90, 8); ctx.fill(); ctx.stroke();
      // Append " น." only when the label is a clock time (date-style labels stand alone).
      const tlab = /\d:\d\d$/.test(labels[i] || '') ? `${labels[i]} น.` : (labels[i] || '');
      if (type === 'line') {
        ctx.fillStyle = col; ctx.font = 'bold 12px "IBM Plex Mono", monospace';
        ctx.fillText(fmtNum(data[i], data[i] > 1000 ? 2 : decimals), bx + 12, padT + 24);
        ctx.fillStyle = 'rgba(255,255,255,0.5)'; ctx.font = '10px "IBM Plex Mono", monospace';
        ctx.fillText(tlab, bx + 12, padT + 40);
      } else {
        const c = candles[i];
        ctx.font = '10px "IBM Plex Mono", monospace';
        ([['O', c.o], ['H', c.h], ['L', c.l], ['C', c.c]] as [string, number][]).forEach((r, ri) => {
          ctx.fillStyle = 'rgba(255,255,255,0.45)';
          ctx.fillText(r[0], bx + 12, padT + 22 + ri * 15);
          ctx.fillStyle = r[0] === 'C' ? (c.c >= c.o ? '#00e0b0' : '#ff3b5c') : 'rgba(255,255,255,0.85)';
          ctx.fillText(fmtNum(r[1], (r[1] as number) > 1000 ? 1 : decimals), bx + 32, padT + 22 + ri * 15);
        });
        ctx.fillStyle = 'rgba(255,255,255,0.5)';
        ctx.fillText(tlab, bx + 12, padT + 86);
      }
    }
  }, [candles, labels, hover, up, decimals, type]);
  return (
    <div ref={wrapRef} className="mm-chartwrap" style={{ height }}
      onMouseMove={e => { const r = wrapRef.current!.getBoundingClientRect(); setHover({ px: e.clientX - r.left }); }}
      onMouseLeave={() => setHover(null)}>
      <canvas ref={ref} style={{ width: '100%', height: '100%' }}></canvas>
    </div>
  );
}

// ---------- DASHBOARD ----------
// Hero-chart timeframes, M15 → 10Y. Each button = a distinct bar size × window
// (~100-300 bars for readability). Most map straight to Yahoo (range, interval);
// H4/MN/3MN/6MN use the server's tf aggregation (Yahoo's 1mo/3mo intervals are
// broken for some symbols, so monthly+ folds from weekly server-side). Yahoo has
// no 3y range → 3Y fetches 5y weekly and trims client-side (trimYears).
// lab picks the x-axis label style: dt = date+time, dm = day/month, my = month/BE-year.
// `mt5`/`days`: the MT5 timeframe and visible window used when the hero draws
// from a broker terminal (gold). MT5 serves whole series, not Yahoo-style
// ranges, so the window has to be trimmed client-side to keep the label true.
const HERO_TFS: { id: string; th: string; en: string; lab: 'dt' | 'dm' | 'my'; range?: string; interval?: string; tf?: string; trimYears?: number; mt5?: string; days?: number }[] = [
  { id: 'M15', range: '5d', interval: '15m', mt5: 'M15', days: 5, th: 'แท่ง 15 นาที · 5 วัน', en: '15-min bars · 5 days', lab: 'dt' },
  { id: 'M30', range: '1mo', interval: '30m', mt5: 'M30', days: 31, th: 'แท่ง 30 นาที · 1 เดือน', en: '30-min bars · 1 month', lab: 'dm' },
  { id: 'H1', range: '1mo', interval: '60m', mt5: 'H1', days: 31, th: 'แท่งรายชั่วโมง · 1 เดือน', en: 'Hourly bars · 1 month', lab: 'dm' },
  { id: 'H4', tf: 'H4', mt5: 'H4', days: 365, th: 'แท่ง 4 ชั่วโมง · 1 ปี', en: '4-hour bars · 1 year', lab: 'dm' },
  { id: 'D1', range: '6mo', interval: '1d', mt5: 'D1', days: 183, th: 'รายวัน · 6 เดือน', en: 'Daily bars · 6 months', lab: 'dm' },
  { id: 'W1', range: '2y', interval: '1wk', mt5: 'W1', days: 731, th: 'รายสัปดาห์ · 2 ปี', en: 'Weekly bars · 2 years', lab: 'my' },
  { id: 'MN', tf: 'MN', mt5: 'MN', days: 3653, th: 'รายเดือน · 10 ปี', en: 'Monthly bars · 10 years', lab: 'my' },
  { id: '3MN', tf: '3MN', mt5: '3MN', th: 'รายไตรมาส · ทั้งหมด', en: 'Quarterly bars · full history', lab: 'my' },
  { id: '6MN', tf: '6MN', mt5: '6MN', th: 'ราย 6 เดือน · ทั้งหมด', en: 'Half-year bars · full history', lab: 'my' },
  { id: '1Y', range: '1y', interval: '1d', mt5: 'D1', days: 365, th: 'รายวัน · 1 ปี', en: 'Daily bars · 1 year', lab: 'dm' },
  { id: '3Y', range: '5y', interval: '1wk', trimYears: 3, mt5: 'W1', days: 1096, th: 'รายสัปดาห์ · 3 ปี', en: 'Weekly bars · 3 years', lab: 'my' },
  { id: '5Y', range: '5y', interval: '1wk', mt5: 'W1', days: 1827, th: 'รายสัปดาห์ · 5 ปี', en: 'Weekly bars · 5 years', lab: 'my' },
  { id: '10Y', range: '10y', interval: '1wk', mt5: 'W1', days: 3653, th: 'รายสัปดาห์ · 10 ปี', en: 'Weekly bars · 10 years', lab: 'my' },
];

// Broker display names for the hero chart's source tag (ids from /api/mt5/sources).
const MT5_BROKER_NAMES: Record<string, string> = {
  pepperstone: 'Pepperstone', icmarkets: 'IC Markets', fxpro: 'FxPro', pi: 'Pi',
};

// The dashboard's four summary cards. Three of them quote curated figures that
// move slowly enough to ship as copy; the gold card quotes a market, and a
// quoted market has to be the live one. Its bundled value was a prototype seed
// that had never moved once — 3,412.60 against a real 4,139 spot on 5 Aug 2026,
// $727 out — so it now reads the same GOLD row the ticker above it prints: MT5's
// own spot while a terminal is running, /api/goldspot otherwise. Subscribing to
// the tick here rather than in DashboardPage keeps the re-render to this row
// instead of the charts.
//
// USD/THB had the same problem as gold: the card printed the prototype's 35.84
// while the indices list two rows above it showed the real rate, 33.60 on
// 1 Oct 2026 - a 6.7% gap on the same page. It now reads that same USDTHB row.
// The crypto total comes from CoinGecko's /global (the browser already asks
// CoinGecko for BTC/ETH/SOL). The Thai 10-year yield has no live source this
// site can read, so its card says it is a reference figure instead of printing
// a change that never moves.
const SUMMARY_LIVE_SYM: Record<string, string> = { XAUUSD: 'GOLD', USDTHB: 'USDTHB' };
// Rows no feed this site can read covers (no broker, no Yahoo ticker): their
// number is the curated one, so the tables say so instead of printing a
// change % that never moves.
const REFERENCE_ONLY = new Set(['TGB10Y']);
function SummaryRow({ en }: { en: boolean }) {
  useLiveTick();
  const t = useT();
  const [total, setTotal] = useState<{ cap: number; chg: number } | null>(null);
  useEffect(() => {
    let alive = true;
    const load = () => fetchCryptoTotal().then(v => { if (alive && v) setTotal(v); });
    load();
    const h = setInterval(load, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(h); };
  }, []);
  return (
    <div className="mm-mini-row">
      {SUMMARY.map((s, i) => {
        let val: string = s.val;
        let chg: number | null = s.chg;
        let reference = false;
        const liveSym = SUMMARY_LIVE_SYM[s.sym];
        if (liveSym) {
          const row = liveSym === 'GOLD' ? ASSETS.find(x => x.sym === 'GOLD') : INDICES.find(x => x.sym === liveSym);
          if (row && MM_LIVE.liveSyms.has(liveSym)) {
            val = row.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            chg = row.chg;
          } else { val = '—'; chg = null; }        // not yet live: no stale seed on show
        } else if (s.sym === 'TOTAL') {
          if (total) { val = fmtBig(total.cap); chg = total.chg; } else { val = '—'; chg = null; }
        } else {
          reference = true;
        }
        const up = (chg ?? 0) >= 0;
        const d = genCandles(s.seed, 100, 42, 0.02).map(c => c.c);
        const dd = up ? d : d.slice().reverse();
        return (
          <div className="mm-card glass mm-mini" key={s.sym} style={{ animationDelay: `${i * 80}ms` }}>
            <div className="mm-mini-head"><span>{en ? (SUMMARY_EN[s.sym] || s.th) : s.th}</span><span className="mm-chip dim mono">{s.sym}</span></div>
            <strong className="mono">{val} <small>{s.unit}</small></strong>
            {reference
              ? <span className="mono dim">{t('ค่าอ้างอิง · ไม่เรียลไทม์', 'Reference figure · not live')}</span>
              : chg == null
                ? <span className="mono dim">—</span>
                : <span className={`mono ${up ? 'up' : 'down'}`}>{up ? '+' : '−'}{Math.abs(chg).toFixed(2)}%</span>}
            {!reference && chg != null && <Sparkline data={dd} w={250} h={54} color={up ? 'var(--up)' : 'var(--down)'}></Sparkline>}
          </div>
        );
      })}
    </div>
  );
}

export function DashboardPage({ onPick, onNav, user, onAuth, onUpgrade }: any) {
  const t = useT();
  const { lang } = useLang();
  const en = lang === 'en';
  const lastTick = MM_LIVE.last || {};
  const classes = ['Stocks', 'Crypto', 'Commodities', 'ETF', 'Bonds'];
  const byClass = (c: string) => ASSETS.filter(a => a.cls === c);
  const movers = [...ASSETS].sort((a, b) => Math.abs(b.chg) - Math.abs(a.chg)).slice(0, 6);
  // No sample headlines while the feed loads or if it fails - they were
  // invented stories stamped "12 minutes ago" under a LIVE badge.
  const newsFeed = useNews([]);
  const news = newsFeed.rows;
  useClock(); // relative timestamps in the feed keep counting on an idle dashboard
  const [heroSym, setHeroSym] = useState('GOLD');
  const hero = INDICES.find(i => i.sym === heroSym)!;
  const [chartType, setChartType] = useState('candle');
  const [heroTf, setHeroTf] = useState('M15');
  const heroCfg = HERO_TFS.find(x => x.id === heroTf) || HERO_TFS[0];
  const heroFallback = useMemo(() => genCandles(hero.seed, hero.price, 85, hero.vol), [hero]);
  const hy = useMemo(() => indexYahoo(hero.sym), [hero]);
  const heroOhlc = useOhlc(
    hy, heroCfg.tf ? { tf: heroCfg.tf } : { range: heroCfg.range, interval: heroCfg.interval }, heroFallback);
  // Gold charts from the broker terminal that owns its printed quote, so the
  // candles and the number are the same metal from the same source — Yahoo's
  // only gold series is GC=F, a December future ~1.4% above spot. Every other
  // hero symbol keeps Yahoo (their quote and series already agree there).
  const goldBroker = hero.sym === 'GOLD' ? mt5OwnerBroker('GOLD') : null;
  const heroMt5 = useMt5Ohlc(goldBroker, 'XAUUSD', heroCfg.mt5 || 'D1');
  const usingMt5Hero = !!goldBroker && heroMt5.live;
  const heroLive = usingMt5Hero || heroOhlc.live;
  // Trim to the labelled window: MT5 serves whole series (days on the chatty
  // TFs, decades on W1), and Yahoo's 3Y comes from a 5y fetch (trimYears).
  // Falls back to the full set if trimming leaves too few bars.
  const heroCandles = useMemo(() => {
    const rows = usingMt5Hero ? heroMt5.rows : heroOhlc.rows;
    const days = usingMt5Hero ? heroCfg.days : (heroOhlc.live && heroCfg.trimYears ? heroCfg.trimYears * 365.25 : 0);
    if (!days) return rows;
    const cut = Date.now() / 1000 - days * 86400;
    const trimmed = rows.filter((c: any) => c.t >= cut);
    return trimmed.length >= 20 ? trimmed : rows;
  }, [usingMt5Hero, heroMt5.rows, heroOhlc.rows, heroOhlc.live, heroCfg]);
  const labels = useMemo(() => heroCandles.map((c: any, i: number) => {
    if (c.t) {
      const d = new Date(c.t * 1000);
      const dm = `${d.getDate()}/${d.getMonth() + 1}`;
      if (heroCfg.lab === 'dt') return `${dm} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
      if (heroCfg.lab === 'my') return `${d.getMonth() + 1}/${String((d.getFullYear() + 543) % 100).padStart(2, '0')}`;
      return dm;
    }
    const tm = 10 * 60 + Math.round(i / (heroCandles.length - 1) * 390);
    return `${Math.floor(tm / 60)}:${String(tm % 60).padStart(2, '0')}`;
  }), [heroCandles, heroCfg.lab]);
  const heroLast = heroCandles[heroCandles.length - 1] || { c: hero.price };
  const heroFirst = heroCandles[0] || { c: hero.price };
  // Gold's headline number is the live spot quote (broker first, /api/goldspot
  // when no terminal runs) — never the last bar of a futures series. Other
  // symbols keep the last bar so the number matches the chart exactly.
  const heroPrice = hero.sym === 'GOLD' ? hero.price : (heroLive ? heroLast.c : hero.price);
  const heroChg = heroLive ? (heroFirst.c ? ((heroLast.c - heroFirst.c) / heroFirst.c) * 100 : 0) : hero.chg;
  // The live % spans the whole visible window (first bar → last), not the day —
  // +7% on a 5-day gold chart next to the index row's daily +2.5% read as a
  // contradiction until the window is named. Each TF label already ends with
  // its window ("แท่ง 15 นาที · 5 วัน"), so quote that part.
  const heroRange = t(heroCfg.th, heroCfg.en).split('·').pop()!.trim();

  return (
    <div className="mm-page" data-screen-label="Dashboard">
      {/* สรุปภาพรวมตลาด: กราฟใหญ่ + ดัชนีหลัก */}
      <div className="mm-hero-grid">
        <div className="mm-card glass mm-hero">
          <div className="mm-hero-head">
            <div className="mm-idx-badge" style={{ background: hero.c }}>{hero.badge}</div>
            <div>
              <div className="mm-asset-symrow"><h2>{en ? hero.name : hero.th}</h2><span className="mm-chip dim mono">{hero.sym}</span></div>
              <span className="mm-asset-name">{t(heroCfg.th, heroCfg.en)} · UTC+7{usingMt5Hero ? ` · ${MT5_BROKER_NAMES[goldBroker!] || goldBroker} (MT5)` : heroLive ? t(' · ราคาจริง (Yahoo)', ' · live (Yahoo)') : ''}</span>
            </div>
            <div className="mm-hero-price">
              <strong className="mono">{fmtNum(heroPrice, 2)} <small>{hero.unit}</small></strong>
              <span className={`mono ${heroChg >= 0 ? 'up' : 'down'}`}>{heroChg >= 0 ? '▲' : '▼'} {Math.abs(heroChg).toFixed(2)}%{heroLive ? <small> / {heroRange}</small> : null}</span>
            </div>
          </div>
          <div className="mm-hero-tfbar">
            <div className="mm-tf">
              {HERO_TFS.map(tf => (
                <button key={tf.id} className={`mono ${heroTf === tf.id ? 'on' : ''}`} onClick={() => setHeroTf(tf.id)} title={t(tf.th, tf.en)}>{tf.id}</button>
              ))}
            </div>
          </div>
          <AreaChart candles={heroCandles} labels={labels} up={heroChg >= 0} height={310} type={chartType}></AreaChart>
          <div className="mm-hero-typebar">
            <div className="mm-tf">
              {[['bar', 'Bar Chart'], ['candle', 'Candlestick'], ['line', 'Line Chart']].map(([k, l]) => (
                <button key={k} className={`mono ${chartType === k ? 'on' : ''}`} onClick={() => setChartType(k)}>{l}</button>
              ))}
            </div>
          </div>
        </div>
        <div className="mm-card glass">
          <SecHead th="ดัชนีหลัก" en="MAJOR INDICES"></SecHead>
          <div className="mm-idxlist">
            {INDICES.map(ix => (
              <button key={ix.sym} className={`mm-idxrow ${heroSym === ix.sym ? 'on' : ''}`} onClick={() => setHeroSym(ix.sym)}>
                <span className="mm-idx-badge sm" style={{ background: ix.c }}>{ix.badge}</span>
                <span className="mm-idxrow-name"><b>{ix.name}</b><em className="mono">{ix.sym}</em></span>
                <span className="mm-idxrow-val mono">
                  <b className={lastTick[ix.sym] ? (lastTick[ix.sym] > 0 ? 'tick-up' : 'tick-dn') : ''} key={'p' + ix.price}>{fmtNum(ix.price, 2)} <small>{ix.unit}</small></b>
                  <em className={ix.chg >= 0 ? 'up' : 'down'}>{ix.chg >= 0 ? '+' : '−'}{Math.abs(ix.chg).toFixed(2)}%</em>
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Bubble Watch — งานวิจัย NOPOLY: ฟองสบู่ 200 ปี + มาตรวัดความเสี่ยงระดับตลาด
          วางไว้เหนือ Super Cycle Time & Duration เพราะเป็นกรอบความเสี่ยงของตลาดทั้งตลาด ควรอ่านก่อน
          ตารางจับจังหวะรายสินทรัพย์ */}
      <MemberGate user={user} onAuth={onAuth} onUpgrade={onUpgrade}
        title={t('Bubble Watch — เฉพาะสมาชิกแบบเสียเงิน', 'Bubble Watch — paid members only')}
        desc={t('วิกฤตตลาดสหรัฐ 200 ปี ความกระจุกตัว มูลค่า และสภาพคล่องดอลลาร์แบบสด สำหรับสมาชิก Gold และ Premium', 'Two centuries of US crises, concentration, valuation and live USD liquidity — Gold and Premium members')}>
        <CardBoundary name="BubbleWatchCard"><BubbleWatchCard></BubbleWatchCard></CardBoundary>
      </MemberGate>

      {/* Super Cycle Time & Duration · NAS100 — ตารางเวลา EA + seasonality (จาก research/) */}
      <MemberGate user={user} onAuth={onAuth} onUpgrade={onUpgrade}
        title={t('Super Cycle Time & Duration · NAS100 — เฉพาะสมาชิกแบบเสียเงิน', 'Super Cycle Time & Duration · NAS100 — paid members only')}
        desc={t('ตารางเวลาเปิด/ปิด EA และสถิติ seasonality 41 ปี สำหรับสมาชิก Gold และ Premium', 'The EA on/off schedule and 41 years of seasonality stats — Gold and Premium members')}>
        <CardBoundary name="SuperTdCard"><SuperTdCard></SuperTdCard></CardBoundary>
      </MemberGate>

      {/* Super Cycle Time & Duration — สถิติ 8 สินทรัพย์จาก engine v0.3.0 (research/supertd/cards) */}
      <MemberGate user={user} onAuth={onAuth} onUpgrade={onUpgrade}
        title={t('Super Cycle Time & Duration · สถิติรายสินทรัพย์ — เฉพาะสมาชิกแบบเสียเงิน', 'Super Cycle Time & Duration per-asset statistics — paid members only')}
        desc={t('สถิติ seasonality 8 สินทรัพย์ พร้อมช่วงความเชื่อมั่นและขนาดตัวอย่าง สำหรับสมาชิก Gold และ Premium', 'Seasonality statistics for 8 instruments with confidence intervals and sample sizes — Gold and Premium members')}>
        <CardBoundary name="SuperTdStats"><SuperTdStats></SuperTdStats></CardBoundary>
      </MemberGate>

      {/* บทเรียนตลาดรายชั่วโมง — nopoly digest (loop learning → research/market-learning) */}
      <MemberGate user={user} onAuth={onAuth} onUpgrade={onUpgrade}
        title={t('บทเรียนตลาดรายวัน — เฉพาะสมาชิกแบบเสียเงิน', 'Daily market learning — paid members only')}
        desc={t('สรุปสิ่งที่เปลี่ยนไปในตลาดทุกเช้า ตี 5 สำหรับสมาชิก Gold และ Premium', 'What changed in the market, distilled every morning at 05:00 — Gold and Premium members')}>
        <CardBoundary name="MarketLearningCard"><MarketLearningCard></MarketLearningCard></CardBoundary>
      </MemberGate>

      {/* Quant Watchlist — งานวิจัยสดผ่าน /api/nopoly?feed=watchlist */}
      <MemberGate user={user} onAuth={onAuth} onUpgrade={onUpgrade}
        title={t('Quant Watchlist — เฉพาะสมาชิกแบบเสียเงิน', 'Quant Watchlist — paid members only')}
        desc={t('เช็กลิสต์ก่อนตลาดเปิด ธีมเทค/AI, IPO, พอร์ตกูรู และกลุ่มพลังงาน สำหรับสมาชิก Gold และ Premium', 'The pre-market checklist, tech/AI themes, IPOs, guru portfolios and power names — Gold and Premium members')}>
        <CardBoundary name="QuantWatchlistCard"><QuantWatchlistCard></QuantWatchlistCard></CardBoundary>
      </MemberGate>

      {/* ธุรกรรมวงใน สว./สส./ผู้บริหาร (FMP disclosure feeds) */}
      <MemberGate user={user} onAuth={onAuth} onUpgrade={onUpgrade}
        title={t('ธุรกรรมวงใน — เฉพาะสมาชิกแบบเสียเงิน', 'Insider & congressional trades — paid members only')}
        desc={t('รายการซื้อขายของ สว./สส./ผู้บริหาร ตามเอกสารเปิดเผยต่อ ก.ล.ต. สำหรับสมาชิก Gold และ Premium', 'Senate, House and executive trades from public disclosures — Gold and Premium members')}>
        <CardBoundary name="GuruTradesCard"><GuruTradesCard></GuruTradesCard></CardBoundary>
      </MemberGate>

      {/* การ์ดสรุป */}
      <SummaryRow en={en}></SummaryRow>

      <div className="mm-grid-main">
        <div className="mm-col">
          {/* Asset classes */}
          {classes.map(c => (
            <div className="mm-card glass" key={c}>
              <SecHead th={CLASS_TH[c]} en={c.toUpperCase()}
                right={<span className="mm-chip dim">{byClass(c).length} {t('รายการ', 'items')}</span>}></SecHead>
              <table className="mm-table hoverable">
                <thead><tr><th>{t('สัญลักษณ์', 'Symbol')}</th><th>{t('ราคา', 'Price')}</th><th>{t('เปลี่ยนแปลง', 'Change')}</th><th>{t('30 วัน', '30D')}</th><th>Master Score</th></tr></thead>
                <tbody>
                  {byClass(c).map(a => (
                    <tr key={a.sym} onClick={() => onPick(a.sym)}>
                      <td><b className="mono">{a.sym}</b><span className="dim mm-cellname">{a.name}</span>
                        {REFERENCE_ONLY.has(a.sym) && <span className="mm-chip dim" title={t('ไม่มีแหล่งราคาเรียลไทม์ — ค่าอ้างอิง', 'No live source — reference figure')}>{t('อ้างอิง', 'ref')}</span>}</td>
                      <td className="mono"><span className={`mm-livecell ${lastTick[a.sym] ? (lastTick[a.sym] > 0 ? 'tick-up' : 'tick-dn') : ''}`} key={'p' + a.price}>{fmtNum(a.price, a.price > 1000 ? 0 : 2)}</span></td>
                      {REFERENCE_ONLY.has(a.sym)
                        ? <td className="mono dim">—</td>
                        : <td className={`mono ${a.chg >= 0 ? 'up' : 'down'}`}>{a.chg >= 0 ? '+' : ''}{a.chg.toFixed(2)}%</td>}
                      <td><Sparkline data={sparkOf(a)} w={96} h={28}></Sparkline></td>
                      <td><div className="mm-scorebar"><i style={{ width: `${a.score}%`, background: a.score >= 75 ? 'var(--up)' : a.score >= 60 ? 'var(--gold)' : 'var(--down)' }}></i><span className="mono">{a.score}</span></div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>

        <div className="mm-col side">
          <div className="mm-card glass">
            <SecHead th="เคลื่อนไหวแรงวันนี้" en="TOP MOVERS"></SecHead>
            <div className="mm-movers">
              {movers.map(a => (
                <button className="mm-mover" key={a.sym} onClick={() => onPick(a.sym)}>
                  <b className="mono">{a.sym}</b>
                  <Sparkline data={sparkOf(a)} w={70} h={22} fill={false}></Sparkline>
                  <span className={`mono ${a.chg >= 0 ? 'up' : 'down'}`}>{a.chg >= 0 ? '+' : ''}{a.chg.toFixed(2)}%</span>
                </button>
              ))}
            </div>
          </div>
          <div className="mm-card glass">
            <SecHead th="ข่าวและเทคนิค" en="NEWS & INSIGHTS" right={<span className="mm-live"><i></i>{t('อัพเดทต่อเนื่อง', 'Live updates')}</span>}></SecHead>
            <div className="mm-newslist">
              {!news.length && <div className="mm-newsrow dim"><span className="mm-impact edu"></span><div><b>{t('กำลังโหลดข่าวล่าสุด…', 'Loading the latest news…')}</b></div></div>}
              {news.map((n: any, i: number) => {
                const isNew = newsFeed.fresh.has(n.url || n.t);
                const Row = (
                  <>
                    <span className={`mm-impact ${n.impact}`}></span>
                    <div><b>{n.t}</b><span className="dim">{n.src} · {ago(n.datetime, en) || n.time + t('ที่แล้ว', ' ago')}</span></div>
                    {isNew && <span className="mm-chip mm-chip-new mono">{t('ใหม่', 'NEW')}</span>}
                    <span className="mm-chip dim">{n.tag}</span>
                  </>
                );
                // Keyed by URL so a story that arrives mid-session doesn't
                // inherit the row (and the entry animation) of whatever was
                // sitting at that index a second ago.
                const key = n.url || n.t || i;
                return n.url ? (
                  <a className={`mm-newsrow ${isNew ? 'fresh' : ''}`} key={key} href={n.url} target="_blank" rel="noopener noreferrer" style={{ animationDelay: `${i * 60}ms` }}>{Row}</a>
                ) : (
                  <div className={`mm-newsrow ${isNew ? 'fresh' : ''}`} key={key} style={{ animationDelay: `${i * 60}ms` }}>{Row}</div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- SCANNER (TradingView-style screener) ----------
// Deterministic derived metrics per asset (volume, rel-vol, multi-timeframe performance, analyst rating)
const _scanCache: Record<string, any> = {};
function scanMetrics(a: any) {
  if (_scanCache[a.sym]) return _scanCache[a.sym];
  const rnd = mmRng(a.seed * 31 + 7);
  const unit = ({ SET: 'THB', mai: 'THB', BOND: '%', JP: 'JPY', HK: 'HKD', EU: 'EUR', IN: 'INR', KR: 'KRW' } as any)[a.market] || 'USD';
  const hue = (a.seed * 47) % 360;
  const mcapNum = a.price * (a.cls === 'Crypto' ? 1e7 : 2.5e8);
  const vol = Math.round((0.3 + rnd()) * (a.cls === 'Crypto' ? 4e4 : 3e7));
  const relVol = +(0.3 + rnd() * 1.3).toFixed(2);
  const mk = (base: number, spread: number) => +((a.chg / 2) + (rnd() - 0.5) * spread + base).toFixed(2);
  const perf = {
    w: mk(0.4, 6), m: mk(1.1, 14), q: mk(2.5, 26), h: mk(4, 38), ytd: mk(6, 52), y: mk(11, 80),
  };
  const epsG = a.eps != null ? +(((rnd() - 0.35) * 90)).toFixed(2) : null;
  const epsGood = epsG != null && epsG > 0;
  let rating, rcls, arrow;
  if (a.score >= 82) { rating = 'มีแรงซื้อรุนแรง'; rcls = 'strong-buy'; arrow = '⤊'; }
  else if (a.score >= 70) { rating = 'มีแรงซื้อ'; rcls = 'buy'; arrow = '↑'; }
  else if (a.score >= 58) { rating = 'เป็นกลาง'; rcls = 'neutral'; arrow = '→'; }
  else { rating = 'มีแรงขาย'; rcls = 'sell'; arrow = '↓'; }
  const rsiV = +(35 + rnd() * 45).toFixed(1);
  const trend = perf.m > 0 ? (perf.q > 0 ? 'ขาขึ้นแข็งแกร่ง' : 'ขาขึ้น') : (perf.q < 0 ? 'ขาลง' : 'พักตัว');
  const out = {
    unit, hue, mcapNum, vol, relVol, perf, epsG, epsGood, rating, rcls, arrow, rsiV, trend,
    ev: mcapNum * (1 + (rnd() - 0.3) * 0.2),
    de: +(2.4 - a.score / 60 + rnd() * 0.4).toFixed(2),
    payout: a.divYield > 0 ? Math.round(25 + rnd() * 55) : 0,
  };
  _scanCache[a.sym] = out;
  return out;
}

const SCAN_PRESETS = [
  { id: 'all', th: 'ทั้งหมด', f: () => true },
  { id: 'value', th: 'หุ้นคุณค่า · P/E < 15', f: (a: any) => a.pe != null && a.pe < 15 },
  { id: 'growth', th: 'เติบโตสูง · Score 80+', f: (a: any) => a.score >= 80 },
  { id: 'dividend', th: 'ปันผลเด่น · Yield > 3%', f: (a: any) => a.divYield > 3 },
  { id: 'momentum', th: 'โมเมนตัมบวก', f: (a: any) => a.chg >= 1 },
  { id: 'oversold', th: 'ย่อตัวแรง', f: (a: any) => a.chg < 0 },
];

const fmtSigned = (v: number, d = 2) => (v >= 0 ? '+' : '') + v.toFixed(d) + '%';
const Pct = ({ v }: { v: number }) => <span className={`mono ${v >= 0 ? 'up' : 'down'}`}>{fmtSigned(v)}</span>;

const COLSETS: Record<string, any> = {
  overview: {
    th: 'ภาพรวม', cols: [
      { k: 'price', t: 'ราคา', s: (a: any) => a.price, r: (a: any) => <span className="mono">{fmtNum(a.price, a.price > 1000 ? 0 : 2)} <i className="mm-unit">{scanMetrics(a).unit}</i></span> },
      { k: 'chg', t: 'เปลี่ยน %', s: (a: any) => a.chg, r: (a: any) => <Pct v={a.chg}></Pct> },
      { k: 'vol', t: 'ปริมาณ', s: (a: any) => scanMetrics(a).vol, r: (a: any) => <span className="mono dim">{fmtBig(scanMetrics(a).vol)}</span> },
      { k: 'relvol', t: 'ปริมาณสัมพัทธ์', s: (a: any) => scanMetrics(a).relVol, r: (a: any) => <span className="mono dim">{scanMetrics(a).relVol.toFixed(2)}</span> },
      { k: 'mcap', t: 'มูลค่าตลาด', s: (a: any) => scanMetrics(a).mcapNum, r: (a: any) => <span className="mono">{a.mcap}</span> },
      { k: 'pe', t: 'P/E', s: (a: any) => a.pe ?? -1, r: (a: any) => <span className="mono">{a.pe == null ? '—' : a.pe.toFixed(2)}</span> },
      { k: 'eps', t: 'EPS dil', s: (a: any) => a.eps ?? -1e9, r: (a: any) => <span className="mono">{a.eps == null ? '—' : a.eps.toFixed(2)}{a.eps != null && <i className="mm-unit"> {scanMetrics(a).unit}</i>}</span> },
      { k: 'epsg', t: 'เติบโต EPS YoY', s: (a: any) => scanMetrics(a).epsG ?? -1e9, r: (a: any) => { const g = scanMetrics(a).epsG; return g == null ? <span className="dim">—</span> : <Pct v={g}></Pct>; } },
      { k: 'yield', t: 'Div Yield', s: (a: any) => a.divYield, r: (a: any) => <span className="mono">{a.divYield ? a.divYield.toFixed(2) + '%' : '—'}</span> },
      { k: 'sector', t: 'หมวดธุรกิจ', align: 'l', s: (a: any) => a.sector, r: (a: any) => <span className="dim mm-sec">{a.sector}</span> },
      { k: 'rating', t: 'คะแนนนักวิเคราะห์', align: 'l', s: (a: any) => a.score, r: (a: any) => { const m = scanMetrics(a); return <span className={`mm-rating ${m.rcls}`}>{m.arrow} {TL(m.rating)}</span>; } },
    ],
  },
  performance: {
    th: 'ประสิทธิภาพ', cols: [
      { k: 'chg', t: 'วันนี้', s: (a: any) => a.chg, r: (a: any) => <Pct v={a.chg}></Pct> },
      { k: 'w', t: '1 สัปดาห์', s: (a: any) => scanMetrics(a).perf.w, r: (a: any) => <Pct v={scanMetrics(a).perf.w}></Pct> },
      { k: 'm', t: '1 เดือน', s: (a: any) => scanMetrics(a).perf.m, r: (a: any) => <Pct v={scanMetrics(a).perf.m}></Pct> },
      { k: 'q', t: '3 เดือน', s: (a: any) => scanMetrics(a).perf.q, r: (a: any) => <Pct v={scanMetrics(a).perf.q}></Pct> },
      { k: 'h', t: '6 เดือน', s: (a: any) => scanMetrics(a).perf.h, r: (a: any) => <Pct v={scanMetrics(a).perf.h}></Pct> },
      { k: 'ytd', t: 'YTD', s: (a: any) => scanMetrics(a).perf.ytd, r: (a: any) => <Pct v={scanMetrics(a).perf.ytd}></Pct> },
      { k: 'y', t: '1 ปี', s: (a: any) => scanMetrics(a).perf.y, r: (a: any) => <Pct v={scanMetrics(a).perf.y}></Pct> },
      { k: 'vol30', t: '30 วัน', align: 'l', s: () => 0, sort: false, r: (a: any) => <Sparkline data={sparkOf(a)} w={92} h={24}></Sparkline> },
    ],
  },
  technical: {
    th: 'ทางเทคนิค', cols: [
      { k: 'price', t: 'ราคา', s: (a: any) => a.price, r: (a: any) => <span className="mono">{fmtNum(a.price, a.price > 1000 ? 0 : 2)}</span> },
      { k: 'chg', t: 'เปลี่ยน %', s: (a: any) => a.chg, r: (a: any) => <Pct v={a.chg}></Pct> },
      { k: 'rsi', t: 'RSI (14)', s: (a: any) => scanMetrics(a).rsiV, r: (a: any) => { const v = scanMetrics(a).rsiV; return <span className={`mono ${v > 70 ? 'down' : v < 30 ? 'up' : ''}`}>{v.toFixed(1)}</span>; } },
      { k: 'beta', t: 'Beta', s: (a: any) => a.beta ?? 0, r: (a: any) => <span className="mono">{a.beta?.toFixed(2) ?? '—'}</span> },
      { k: 'trend', t: 'แนวโน้ม', align: 'l', s: (a: any) => scanMetrics(a).perf.q, r: (a: any) => { const tr = scanMetrics(a).trend; const up = tr.includes('ขึ้น'); return <span className={`mono ${up ? 'up' : tr.includes('ลง') ? 'down' : 'dim'}`}>{TL(tr)}</span>; } },
      { k: 'score', t: 'สัญญาณรวม', align: 'l', s: (a: any) => a.score, r: (a: any) => { const m = scanMetrics(a); return <span className={`mm-rating ${m.rcls}`}>{m.arrow} {TL(m.rcls === 'strong-buy' ? 'ซื้อรุนแรง' : m.rcls === 'buy' ? 'ซื้อ' : m.rcls === 'neutral' ? 'ถือ' : 'ขาย')}</span>; } },
      { k: 'spark', t: '30 วัน', align: 'l', s: () => 0, sort: false, r: (a: any) => <Sparkline data={sparkOf(a)} w={92} h={24}></Sparkline> },
    ],
  },
  valuation: {
    th: 'การประเมินมูลค่า', cols: [
      { k: 'mcap', t: 'มูลค่าตลาด', s: (a: any) => scanMetrics(a).mcapNum, r: (a: any) => <span className="mono">{a.mcap}</span> },
      { k: 'pe', t: 'P/E', s: (a: any) => a.pe ?? -1, r: (a: any) => <span className="mono">{a.pe == null ? '—' : a.pe.toFixed(2)}</span> },
      { k: 'pb', t: 'P/BV', s: (a: any) => a.pb ?? -1, r: (a: any) => <span className="mono">{a.pb == null ? '—' : a.pb.toFixed(2)}</span> },
      { k: 'eps', t: 'EPS', s: (a: any) => a.eps ?? -1e9, r: (a: any) => <span className="mono">{a.eps == null ? '—' : a.eps.toFixed(2)}</span> },
      { k: 'roe', t: 'ROE', s: (a: any) => a.roe ?? -1, r: (a: any) => <span className="mono">{a.roe == null ? '—' : a.roe.toFixed(1) + '%'}</span> },
      { k: 'de', t: 'D/E', s: (a: any) => scanMetrics(a).de, r: (a: any) => <span className="mono">{a.cls === 'Stocks' ? scanMetrics(a).de.toFixed(2) : '—'}</span> },
      { k: 'ev', t: 'EV', align: 'l', s: (a: any) => scanMetrics(a).ev, r: (a: any) => <span className="mono dim">{fmtBig(scanMetrics(a).ev)}</span> },
    ],
  },
  dividends: {
    th: 'เงินปันผล', cols: [
      { k: 'price', t: 'ราคา', s: (a: any) => a.price, r: (a: any) => <span className="mono">{fmtNum(a.price, a.price > 1000 ? 0 : 2)}</span> },
      { k: 'yield', t: 'Div Yield', s: (a: any) => a.divYield, r: (a: any) => <span className={`mono ${a.divYield > 3 ? 'up' : ''}`}>{a.divYield ? a.divYield.toFixed(2) + '%' : '—'}</span> },
      { k: 'dps', t: 'ปันผล/หุ้น', s: (a: any) => a.price * a.divYield / 100, r: (a: any) => <span className="mono">{a.divYield ? (a.price * a.divYield / 100).toFixed(2) : '—'}</span> },
      { k: 'payout', t: 'Payout', s: (a: any) => scanMetrics(a).payout, r: (a: any) => <span className="mono dim">{scanMetrics(a).payout ? scanMetrics(a).payout + '%' : '—'}</span> },
      { k: 'yieldbar', t: 'อันดับ Yield', align: 'l', s: (a: any) => a.divYield, sort: false, r: (a: any) => <div className="mm-scorebar yield"><i style={{ width: `${Math.min(100, a.divYield * 14)}%`, background: 'var(--up)' }}></i></div> },
      { k: 'sector', t: 'หมวดธุรกิจ', align: 'l', s: (a: any) => a.sector, r: (a: any) => <span className="dim mm-sec">{a.sector}</span> },
    ],
  },
};

const SCAN_FILTERS = [
  { id: 'index', label: 'ทั้งหมด', opts: [
    { l: 'หุ้นไทย (SET)', f: (a: any) => a.market === 'SET' || a.market === 'mai' },
    { l: 'หุ้นสหรัฐ (US)', f: (a: any) => a.market === 'US' && a.cls === 'Stocks' },
    { l: 'ญี่ปุ่น (JP)', f: (a: any) => a.market === 'JP' },
    { l: 'ฮ่องกง/จีน (HK)', f: (a: any) => a.market === 'HK' },
    { l: 'ยุโรป (EU)', f: (a: any) => a.market === 'EU' },
    { l: 'อินเดีย (IN)', f: (a: any) => a.market === 'IN' },
    { l: 'เกาหลี (KR)', f: (a: any) => a.market === 'KR' },
    { l: 'Crypto', f: (a: any) => a.cls === 'Crypto' },
    { l: 'ETF', f: (a: any) => a.cls === 'ETF' },
  ] },
  { id: 'price', label: 'ราคา', opts: [
    { l: 'ต่ำกว่า 50', f: (a: any) => a.price < 50 },
    { l: '50 – 200', f: (a: any) => a.price >= 50 && a.price <= 200 },
    { l: '200 – 1,000', f: (a: any) => a.price > 200 && a.price <= 1000 },
    { l: 'มากกว่า 1,000', f: (a: any) => a.price > 1000 },
  ] },
  { id: 'chg', label: 'เปลี่ยนแปลง %', opts: [
    { l: 'บวก (ขึ้น)', f: (a: any) => a.chg > 0 },
    { l: 'ลบ (ลง)', f: (a: any) => a.chg < 0 },
    { l: 'ขึ้นแรง > +2%', f: (a: any) => a.chg > 2 },
    { l: 'ลงแรง < -2%', f: (a: any) => a.chg < -2 },
  ] },
  { id: 'mcap', label: 'มูลค่าตามราคาตลาด', opts: [
    { l: 'ขนาดใหญ่ (Mega)', f: (a: any) => scanMetrics(a).mcapNum >= 1e12 },
    { l: 'ขนาดกลาง', f: (a: any) => scanMetrics(a).mcapNum >= 1e11 && scanMetrics(a).mcapNum < 1e12 },
    { l: 'ขนาดเล็ก', f: (a: any) => scanMetrics(a).mcapNum < 1e11 },
  ] },
  { id: 'pe', label: 'P/E', opts: [
    { l: 'ถูก · ต่ำกว่า 15', f: (a: any) => a.pe != null && a.pe < 15 },
    { l: 'ปานกลาง · 15–30', f: (a: any) => a.pe != null && a.pe >= 15 && a.pe <= 30 },
    { l: 'สูง · มากกว่า 30', f: (a: any) => a.pe != null && a.pe > 30 },
  ] },
  { id: 'epsg', label: 'การเติบโต EPS', opts: [
    { l: 'เติบโตบวก', f: (a: any) => (scanMetrics(a).epsG ?? -1) > 0 },
    { l: 'เติบโตสูง > 20%', f: (a: any) => (scanMetrics(a).epsG ?? -1) > 20 },
    { l: 'หดตัว (ติดลบ)', f: (a: any) => (scanMetrics(a).epsG ?? 1) < 0 },
  ] },
  { id: 'yield', label: 'Div yield %', opts: [
    { l: 'มีปันผล', f: (a: any) => a.divYield > 0 },
    { l: 'มากกว่า 3%', f: (a: any) => a.divYield > 3 },
    { l: 'มากกว่า 5%', f: (a: any) => a.divYield > 5 },
  ] },
  { id: 'sector', label: 'หมวดธุรกิจ', opts: [
    { l: 'หุ้น', f: (a: any) => a.cls === 'Stocks' },
    { l: 'คริปโต', f: (a: any) => a.cls === 'Crypto' },
    { l: 'สินค้าโภคภัณฑ์', f: (a: any) => a.cls === 'Commodities' },
    { l: 'กองทุน ETF', f: (a: any) => a.cls === 'ETF' },
    { l: 'ตราสารหนี้', f: (a: any) => a.cls === 'Bonds' },
  ] },
  { id: 'rating', label: 'คะแนนนักวิเคราะห์', prem: true, opts: [
    { l: 'มีแรงซื้อรุนแรง', f: (a: any) => a.score >= 82 },
    { l: 'มีแรงซื้อ', f: (a: any) => a.score >= 70 },
    { l: 'เป็นกลาง', f: (a: any) => a.score >= 58 && a.score < 70 },
    { l: 'มีแรงขาย', f: (a: any) => a.score < 58 },
  ] },
  { id: 'perf', label: 'ประสิทธิภาพ %', prem: true, opts: [
    { l: '1 เดือนบวก', f: (a: any) => scanMetrics(a).perf.m > 0 },
    { l: '1 เดือน > 10%', f: (a: any) => scanMetrics(a).perf.m > 10 },
    { l: 'YTD บวก', f: (a: any) => scanMetrics(a).perf.ytd > 0 },
  ] },
  { id: 'peg', label: 'PEG', prem: true, opts: [
    { l: 'ต่ำกว่า 1 (น่าสนใจ)', f: (a: any) => a.pe != null && scanMetrics(a).epsG > 0 && (a.pe / scanMetrics(a).epsG) < 1 },
    { l: 'ต่ำกว่า 2', f: (a: any) => a.pe != null && scanMetrics(a).epsG > 0 && (a.pe / scanMetrics(a).epsG) < 2 },
  ] },
  { id: 'beta', label: 'เบต้า', opts: [
    { l: 'ผันผวนต่ำ < 1', f: (a: any) => a.beta != null && a.beta < 1 },
    { l: 'ผันผวนปานกลาง 1–1.5', f: (a: any) => a.beta != null && a.beta >= 1 && a.beta <= 1.5 },
    { l: 'ผันผวนสูง > 1.5', f: (a: any) => a.beta != null && a.beta > 1.5 },
  ] },
];

export function ScannerPage({ onPick, user, onUpgrade, onAuth }: any) {
  const t = useT();
  useLiveTick(); // live-recompute scanner metrics from streaming quotes
  const { lang } = useLang();
  _scanEn = lang === 'en';
  const tl = TL;
  const [preset, setPreset] = useState('all');
  const [cls, setCls] = useState('ทุก Asset');
  const [view, setView] = useState('overview');
  const [sortK, setSortK] = useState('chg');
  const [dir, setDir] = useState(-1);
  const [filters, setFilters] = useState<Record<string, number | undefined>>({});
  const [openChip, setOpenChip] = useState<string | null>(null);
  const classes = ['ทุก Asset', 'Stocks', 'Crypto', 'Commodities', 'ETF', 'Bonds'];
  const colset = COLSETS[view].cols;

  const rows = useMemo(() => {
    let r = ASSETS.filter(SCAN_PRESETS.find(p => p.id === preset)!.f);
    if (cls !== 'ทุก Asset') r = r.filter(a => a.cls === cls);
    SCAN_FILTERS.forEach(fl => {
      const oi = filters[fl.id];
      if (oi != null && fl.opts[oi]) r = r.filter(fl.opts[oi].f);
    });
    const col = colset.find((c: any) => c.k === sortK);
    const sf = col ? col.s : ((a: any) => a.score);
    return [...r].sort((a, b) => ((sf(a) ?? -1e9) - (sf(b) ?? -1e9)) * dir);
  }, [preset, cls, sortK, dir, view, filters]);

  const activeCount = Object.values(filters).filter(v => v != null).length;
  const setFilter = (id: string, oi: number) => { setFilters(f => ({ ...f, [id]: f[id] === oi ? undefined : oi })); setOpenChip(null); };
  const clearAll = () => { setFilters({}); setOpenChip(null); };

  const sortBy = (k: string, sortable?: boolean) => { if (sortable === false) return; if (sortK === k) setDir(-dir); else { setSortK(k); setDir(-1); } };

  return (
    <div className="mm-page mm-scanner" data-screen-label="Scanner">
      {/* กลุ่มหุ้นเด่นยุค AI — curated themes + ราคาสด (แนวคิดจาก Featured Stocks) */}
      <CardBoundary name="FeaturedGroups"><FeaturedGroups></FeaturedGroups></CardBoundary>

      <div className="mm-card glass mm-scan-card">
        <div className="mm-scan-top">
          <div className="mm-scan-title">
            <h2>{t('ตัวคัดกรองหุ้น', 'Stock Screener')}</h2>
            <span className="mono dim">{rows.length} / {ASSETS.length} {t('หลักทรัพย์', 'securities')}</span>
          </div>
          <div className="mm-filterrow">
            {SCAN_PRESETS.map(p => (
              <button key={p.id} className={`mm-pill ${preset === p.id ? 'on' : ''}`} onClick={() => setPreset(p.id)}>{tl(p.th)}</button>
            ))}
          </div>
        </div>

        <div className="mm-scan-chips">
          {SCAN_FILTERS.map(fl => {
            const oi = filters[fl.id];
            const active = oi != null;
            const locked = (fl as any).prem && !mmIsPremium(user);
            return (
              <div className="mm-fchip-wrap" key={fl.id}>
                <button className={`mm-fchip ${active ? 'on' : ''} ${openChip === fl.id ? 'open' : ''} ${locked ? 'locked' : ''}`}
                  onClick={() => locked ? (user ? onUpgrade() : onAuth('signup')) : setOpenChip(openChip === fl.id ? null : fl.id)}>
                  {active ? tl(fl.opts[oi!].l) : tl(fl.label)}
                  {locked ? <i className="mm-pro">PRO</i> : active ? <i className="mm-fchip-x" onClick={e => { e.stopPropagation(); setFilter(fl.id, oi!); }}>✕</i> : <i>▾</i>}
                </button>
                {!locked && openChip === fl.id && (
                  <div className="mm-fchip-menu">
                    {fl.opts.map((o, i) => (
                      <button key={i} className={oi === i ? 'on' : ''} onClick={() => setFilter(fl.id, i)}>{tl(o.l)}</button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {activeCount > 0 && <button className="mm-fchip clear" onClick={clearAll}>{t('ล้างตัวกรอง', 'Clear filters')} ({activeCount}) ✕</button>}
        </div>

        <div className="mm-scan-tabs">
          <div className="mm-scan-classes">
            {classes.map(c => (
              <button key={c} className={`mm-pill alt sm ${cls === c ? 'on' : ''}`} onClick={() => setCls(c)}>{tl(c)}</button>
            ))}
          </div>
          <div className="mm-scan-views">
            {Object.entries(COLSETS).map(([k, v]: any) => (
              <button key={k} className={`mm-vtab ${view === k ? 'on' : ''}`} onClick={() => { setView(k); setSortK(v.cols[1] ? v.cols[1].k : v.cols[0].k); setDir(-1); }}>{tl(v.th)}</button>
            ))}
          </div>
        </div>

        <div className="mm-scan-scroll">
          <table className="mm-scantbl">
            <thead>
              <tr>
                <th className="sticky-l">{t('สัญลักษณ์', 'Symbol')} <i className="mm-symcount mono">{rows.length}</i></th>
                {colset.map((c: any) => (
                  <th key={c.k} className={`${c.align === 'l' ? 'l' : 'r'} ${c.sort === false ? '' : 'sortable'} ${sortK === c.k ? 'on' : ''}`} onClick={() => sortBy(c.k, c.sort)}>
                    {tl(c.t)}{sortK === c.k ? <i className="sarr">{dir === -1 ? ' ↓' : ' ↑'}</i> : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={colset.length + 1} className="mm-scan-empty">{t('ไม่พบหลักทรัพย์ที่ตรงกับตัวกรอง — ลองปรับหรือล้างตัวกรอง', 'No securities match the filters — adjust or clear them')}</td></tr>
              )}
              {rows.map((a, i) => (
                <tr key={a.sym} onClick={() => onPick(a.sym)} className="mm-fadein-row" style={{ animationDelay: `${i * 22}ms` }}>
                  <td className="sticky-l mm-scan-sym">
                    {/* assetYahoo also gives the FMP CDN key for equities; crypto,
                        gold, oil and bonds return no logo and keep the initial. */}
                    <SymLogo sym={a.sym} cdnSym={assetYahoo(a)}
                      style={{ background: `hsl(${scanMetrics(a).hue} 55% 22%)`, color: `hsl(${scanMetrics(a).hue} 80% 65%)`, borderColor: `hsl(${scanMetrics(a).hue} 60% 40%)` }}></SymLogo>
                    <span className="mm-scan-symtxt"><b className="mono">{a.sym}{a.divYield > 0 && <i className="mm-dbadge">D</i>}</b><em>{a.name}</em></span>
                  </td>
                  {colset.map((c: any) => (
                    <td key={c.k} className={c.align === 'l' ? 'l' : 'r'}>{c.r(a)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ---------- IPO (TradingView-style global calendar) ----------
const TH_MONTHS = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
const TH_DOW = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัส', 'ศุกร์', 'เสาร์'];
const EN_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const EN_DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
// Calendars use module-level date helpers; each calendar component sets _calEn
// at the top of render so months/years/weekdays localize (Thai uses BE year).
let _calEn = false;
const MON = (i: number) => (_calEn ? EN_MONTHS[i] : TH_MONTHS[i]);
const yearLabel = (y: number) => (_calEn ? y : y + 543);
const isoOf = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const parseIso = (s: string) => { const [y, m, dd] = s.split('-').map(Number); return new Date(y, m - 1, dd); };
const fmtThaiDate = (d: Date) => _calEn
  ? `${EN_DOW[d.getDay()]}, ${EN_MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`
  : `${TH_DOW[d.getDay()]}ที่ ${d.getDate()} ${TH_MONTHS[d.getMonth()]} ${d.getFullYear() + 543}`;
const startOfWeek = (d: Date) => { const x = new Date(d); x.setDate(x.getDate() - x.getDay()); x.setHours(0, 0, 0, 0); return x; };
const STATUS_TH: Record<string, string> = { Pricing: 'กำหนดราคา', Roadshow: 'โรดโชว์', Bookbuild: 'สำรวจราคา', Filing: 'ยื่นไฟลิ่ง' };
const STATUS_EN: Record<string, string> = { Pricing: 'Pricing', Roadshow: 'Roadshow', Bookbuild: 'Bookbuild', Filing: 'Filing' };
const WHEN_EN: Record<string, string> = { 'หลังปิด': 'After close', 'ก่อนเปิด': 'Before open' };
const STATUS = (k: string) => (_calEn ? (STATUS_EN[k] || k) : (STATUS_TH[k] || k));

// The economic, earnings, revenue and dividend tabs used to be filled by seeded
// random generators: sixteen stock events ("Fed Interest Rate Decision", "US CPI")
// dealt onto days by dice with invented forecasts, made-up EPS and dividends,
// and per-day counts (14, 23, 114...) that were the same dice. They read the real
// calendars now - the same feeds as the Earnings and Dividends pages, plus this
// week's economic releases (server/econcalendar.mjs).

const CO_FLAG: Record<string, string> = { US: '🇺🇸', TH: '🇹🇭', JP: '🇯🇵', HK: '🇭🇰', CN: '🇨🇳', EU: '🇪🇺', UK: '🇬🇧', GB: '🇬🇧', KR: '🇰🇷', IN: '🇮🇳' };
const CAL_TABS = [
  { id: 'eco', label: 'ทางเศรษฐกิจ', en: 'Economic', countKey: 'eco' },
  { id: 'earn', label: 'ผลประกอบการ', en: 'Earnings', countKey: 'earn' },
  { id: 'rev', label: 'รายได้', en: 'Revenue', countKey: 'earn' },
  { id: 'div', label: 'เงินปันผล', en: 'Dividends', countKey: 'div' },
  { id: 'ipo', label: 'IPO', en: 'IPO', countKey: 'ipo' },
];

export function IpoPage({ onPick }: any) {
  const t = useT();
  const { lang } = useLang();
  _calEn = lang === 'en';
  const [anchor, setAnchor] = useState(() => new Date());
  const [tableSort, setTableSort] = useState({ k: 'd', dir: 1 });
  const [calTab, setCalTab] = useState('ipo');
  const weekStart = useMemo(() => startOfWeek(anchor), [anchor]);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; }), [weekStart]);
  const todayIso = isoOf(new Date());

  // Real calendars for the window around the displayed week: IPOs (Finnhub),
  // earnings (Finnhub), ex-dividend dates (FMP) and this week's economic
  // releases. No sample rows: an empty day is empty, a failed feed says so.
  const winFrom = useMemo(() => { const d = new Date(weekStart); d.setDate(d.getDate() - 14); return isoOf(d); }, [weekStart]);
  const winTo = useMemo(() => { const d = new Date(weekStart); d.setDate(d.getDate() + 28); return isoOf(d); }, [weekStart]);
  const ipoCal = useIpo(winFrom, winTo);
  const ipoRows = ipoCal.rows;
  const earnCal = useEarnings(winFrom, winTo);
  const divCal = useDividends(winFrom, winTo);
  const econ = useEconWeek();

  const ipoByDay = useMemo(() => {
    const m: Record<string, any[]> = {};
    ipoRows.forEach((p: any) => { (m[p.d] = m[p.d] || []).push(p); });
    return m;
  }, [ipoRows]);
  // Every tab's rows by the reader's own calendar day. Earnings are de-duplicated:
  // the feed repeats some tickers (BTLN, CABI, POM... twice on one day).
  const byDay = useMemo(() => {
    const group = (rows: any[], dayOf: (r: any) => string | null) => {
      const m: Record<string, any[]> = {};
      for (const r of rows) { const k = dayOf(r); if (k) (m[k] = m[k] || []).push(r); }
      return m;
    };
    const seen = new Set<string>();
    const earn = earnCal.rows.filter((e: any) => { const k = `${e.t}|${e.d}|${e.sess}`; if (seen.has(k)) return false; seen.add(k); return true; });
    return {
      eco: group(econ.events.filter(e => !e.holiday), e => isoOf(new Date(e.at))),
      earn: group(earn, e => e.d),
      div: group(divCal.rows, d => d.d || null),
    };
  }, [earnCal.rows, divCal.rows, econ.events]);
  // The economic feed covers the current week only; outside it a count is unknown, not zero.
  const econWeek = useMemo(() => {
    if (!econ.events.length) return null;
    const ds = econ.events.map(e => isoOf(new Date(e.at))).sort();
    return { from: ds[0], to: ds[ds.length - 1] };
  }, [econ.events]);

  const shiftWeek = (n: number) => { const d = new Date(weekStart); d.setDate(d.getDate() + n * 7); setAnchor(d); };
  const goToday = () => setAnchor(parseIso(todayIso));
  const onMonth = (e: any) => { const d = new Date(weekStart); d.setMonth(+e.target.value); setAnchor(d); };
  const onYear = (e: any) => { const d = new Date(weekStart); d.setFullYear(+e.target.value); setAnchor(d); };

  const sortedAll = useMemo(() => {
    const { k, dir } = tableSort;
    const val = (p: any) => k === 'd' ? p.d : k === 'value' ? p.valNum : k === 'sym' ? p.sym : k === 'market' ? p.market : k === 'heat' ? p.heat : p.d;
    return [...ipoRows].sort((a, b) => { const x = val(a), y = val(b); return (x < y ? -1 : x > y ? 1 : 0) * dir; });
  }, [tableSort, ipoRows]);
  const sortBy = (k: string) => setTableSort(s => ({ k, dir: s.k === k ? -s.dir : 1 }));
  const Arrow = ({ k }: { k: string }) => tableSort.k === k ? <i className="sarr">{tableSort.dir === 1 ? ' ↑' : ' ↓'}</i> : null;

  const weekLabel = `${weekStart.getDate()} ${MON(weekStart.getMonth())} — ${days[6].getDate()} ${MON(days[6].getMonth())} ${yearLabel(days[6].getFullYear())}`;

  return (
    <div className="mm-page" data-screen-label="IPO">
      <div className="mm-card glass mm-ipo-cal">
        <div className="mm-ipo-toolbar">
          <div className="mm-ipo-nav">
            <button className="mm-ipo-today" onClick={goToday}>{t('วันนี้', 'Today')}</button>
            <button className="mm-ipo-arrow" onClick={() => shiftWeek(-1)} aria-label={t('สัปดาห์ก่อน', 'Previous week')}>‹</button>
            <button className="mm-ipo-arrow" onClick={() => shiftWeek(1)} aria-label={t('สัปดาห์ถัดไป', 'Next week')}>›</button>
            <strong className="mm-ipo-range">{weekLabel}</strong>
          </div>
          <div className="mm-ipo-pickers">
            <select className="mm-ipo-select" value={weekStart.getMonth()} onChange={onMonth}>
              {(lang === 'en' ? EN_MONTHS : TH_MONTHS).map((m, i) => <option key={i} value={i}>{m}</option>)}
            </select>
            <select className="mm-ipo-select" value={weekStart.getFullYear()} onChange={onYear}>
              {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{yearLabel(y)}</option>)}
            </select>
            <span className="mm-chip">🌐 {t('ทั่วโลก', 'Global')}</span>
          </div>
        </div>

        <div className="mm-week">
          {days.map((d) => {
            const iso = isoOf(d);
            const inEcon = !!econWeek && iso >= econWeek.from && iso <= econWeek.to;
            const c = {
              eco: inEcon ? (byDay.eco[iso] || []).length : null,
              earn: earnCal.live ? (byDay.earn[iso] || []).length : null,
              div: divCal.live ? (byDay.div[iso] || []).length : null,
            };
            const ipos = ipoByDay[iso] || [];
            const isToday = iso === todayIso;
            const isSel = iso === isoOf(anchor);
            return (
              <button key={iso} className={`mm-wday ${isToday ? 'today' : ''} ${isSel ? 'sel' : ''}`} onClick={() => setAnchor(d)}>
                <div className="mm-wday-h"><span>{lang === 'en' ? EN_DOW[d.getDay()] : TH_DOW[d.getDay()].slice(0, d.getDay() === 4 ? 4 : 3)}</span><b>{d.getDate()}</b></div>
                <div className="mm-wday-rows mono">
                  <span><i>{t('เศรษฐกิจ', 'Economic')}</i><em>{c.eco || '—'}</em></span>
                  <span><i>{t('ผลประกอบการ', 'Earnings')}</i><em>{c.earn || '—'}</em></span>
                  <span><i>{t('เงินปันผล', 'Dividends')}</i><em>{c.div || '—'}</em></span>
                  <span className={ipos.length ? 'hot' : ''}><i>IPO</i><em>{ipos.length || '—'}</em></span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mm-card glass">
        <div className="mm-ipo-tabs">
          {CAL_TABS.map(tab => (
            <button key={tab.id} className={`mm-ipo-tab ${calTab === tab.id ? 'on' : ''}`} onClick={() => setCalTab(tab.id)}>{lang === 'en' ? tab.en : tab.label}</button>
          ))}
        </div>
        {(() => {
          const dayEvents = (d: Date) => {
            const iso = isoOf(d);
            if (calTab === 'ipo') return ipoByDay[iso] || [];
            if (calTab === 'eco') return byDay.eco[iso] || [];
            if (calTab === 'earn' || calTab === 'rev') return byDay.earn[iso] || [];
            if (calTab === 'div') return byDay.div[iso] || [];
            return [];
          };
          const activeDays = days.filter(d => dayEvents(d).length);
          if (!activeDays.length) {
            const feed = calTab === 'ipo' ? ipoCal : calTab === 'div' ? divCal : calTab === 'eco' ? null : earnCal;
            const msg = feed?.loading || (calTab === 'eco' && !econ.loaded)
              ? t('กำลังโหลด…', 'Loading…')
              : feed?.failed || (calTab === 'eco' && econ.failed && !econ.events.length)
                ? t('โหลดปฏิทินไม่สำเร็จ — ลองรีเฟรชอีกครั้งในสักครู่', 'Could not load the calendar — try again in a moment')
                : calTab === 'eco' && !(econWeek && days.some(d => { const k = isoOf(d); return k >= econWeek.from && k <= econWeek.to; }))
                  ? t('ปฏิทินเศรษฐกิจมีเฉพาะสัปดาห์ปัจจุบัน — กด "วันนี้" เพื่อกลับมาสัปดาห์นี้', 'The economic calendar covers the current week only — press "Today" to come back')
                  : t('ไม่มีเหตุการณ์ในสัปดาห์นี้ — ลองเลือกสัปดาห์อื่น', 'No events this week — try another week');
            return <div className="mm-ipo-none">⊘ {msg}</div>;
          }
          return activeDays.map(d => {
            const iso = isoOf(d);
            const evs = dayEvents(d) as any[];
            return (
              <div className="mm-ipo-dayblock" key={iso}>
                <div className="mm-ipo-dayhdr">{fmtThaiDate(d)}</div>
                <table className="mm-ipotbl">
                  {calTab === 'ipo' && <thead><tr><th className="l">{t('บริษัท', 'Company')}</th><th className="l">{t('ตลาดหลักทรัพย์', 'Exchange')}</th><th>{t('ช่วงราคา', 'Price range')}</th><th>{t('หุ้นที่เสนอขาย', 'Shares offered')}</th><th>{t('มูลค่าระดมทุน', 'Raise')}</th><th className="l">{t('สถานะ', 'Status')}</th></tr></thead>}
                  {calTab === 'eco' && <thead><tr><th className="l">{t('เหตุการณ์', 'Event')}</th><th className="l">{t('ประเทศ', 'Country')}</th><th>{t('เวลา', 'Time')}</th><th>{t('ความสำคัญ', 'Impact')}</th><th>{t('ครั้งก่อน', 'Previous')}</th><th>{t('คาดการณ์', 'Forecast')}</th></tr></thead>}
                  {(calTab === 'earn' || calTab === 'rev') && <thead><tr><th className="l">{t('บริษัท', 'Company')}</th><th className="l">{t('ตลาด', 'Market')}</th><th>{t('ช่วงเวลา', 'Session')}</th><th>{calTab === 'rev' ? t('รายได้คาด', 'Rev est.') : t('EPS คาด', 'EPS est.')}</th><th>{calTab === 'rev' ? t('รายได้ก่อน', 'Rev prior') : t('EPS ก่อน', 'EPS prior')}</th><th>Market Cap</th></tr></thead>}
                  {calTab === 'div' && <thead><tr><th className="l">{t('หลักทรัพย์', 'Security')}</th><th className="l">{t('ประเทศ', 'Country')}</th><th>{t('เงินปันผล/หุ้น', 'Div/share')}</th><th>Yield</th><th className="l">{t('วันจ่าย', 'Pay date')}</th></tr></thead>}
                  <tbody>
                    {calTab === 'ipo' && evs.map(p => (
                      <tr key={p.sym}>
                        <td className="l mm-ipo-co"><SymLogo sym={p.sym} cdnSym={p.sym} className="mm-ipo-logo" optional></SymLogo><span className="mm-ipo-tick mono">{p.sym}</span><span className="mm-ipo-coname">{p.name}</span></td>
                        <td className="l"><span className="mm-ipo-mkt">{p.flag} {p.market}</span></td>
                        <td className="mono">{p.cur}{p.range}</td>
                        <td className="mono">{p.shares && p.shares !== '0' ? p.shares : '—'}</td>
                        <td className="mono">{p.value && p.value !== '0' ? p.cur + p.value : '—'}</td>
                        <td className="l"><span className={`mm-status s-${p.status.toLowerCase()}`}>{STATUS(p.status)}</span></td>
                      </tr>
                    ))}
                    {calTab === 'eco' && evs.map((e, i) => (
                      <tr key={i}>
                        <td className="l mm-ipo-coname">{e.title}</td>
                        <td className="l"><span className="mm-ipo-mkt">{e.flag} {e.country}</span></td>
                        <td className="mono">{new Date(e.at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</td>
                        <td><span className="mm-impdots">{[1, 2, 3].map(x => <i key={x} className={x <= e.impact ? 'on' : ''}></i>)}</span></td>
                        <td className="mono dim">{e.previous || '—'}</td>
                        <td className="mono">{e.forecast || '—'}</td>
                      </tr>
                    ))}
                    {(calTab === 'earn' || calTab === 'rev') && evs.map((e, i) => (
                      <tr key={i}>
                        <td className="l mm-ipo-co"><SymLogo sym={e.t} cdnSym={e.t} className="mm-ipo-logo" optional></SymLogo><span className="mm-ipo-tick mono">{e.t}</span><span className="mm-ipo-coname">{e.n}</span></td>
                        <td className="l"><span className="mm-ipo-mkt">{CO_FLAG[e.co] || '🌐'} {e.co || ''}</span></td>
                        <td><span className={`mm-status ${e.sess === 'bmo' ? 's-bookbuild' : 's-pricing'}`}>{e.sess === 'bmo' ? '☀ ' + t('ก่อนเปิด', 'Before open') : e.sess === 'amc' ? '🌙 ' + t('หลังปิด', 'After close') : t('ระหว่างวัน', 'During the day')}</span></td>
                        <td className="mono">{calTab === 'rev' ? (e.revEst || '—') : (e.est || '—')}</td>
                        <td className="mono dim">{calTab === 'rev' ? (e.rev || '—') : (e.last || '—')}</td>
                        <td className="mono">{e.mcap || '—'}</td>
                      </tr>
                    ))}
                    {calTab === 'div' && evs.map((e, i) => (
                      <tr key={i}>
                        <td className="l mm-ipo-co"><SymLogo sym={e.sym} cdnSym={e.sym} className="mm-ipo-logo" optional></SymLogo><span className="mm-ipo-tick mono">{e.sym}</span><span className="mm-ipo-coname">{e.name}</span></td>
                        <td className="l"><span className="mm-ipo-mkt">{e.flag} {e.co || ''}</span></td>
                        <td className="mono">{e.amount}</td>
                        <td className="mono up">{typeof e.yield === 'number' && e.yield > 0 ? e.yield.toFixed(2) + '%' : '—'}</td>
                        <td className="l dim">{e.payDate}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          });
        })()}
      </div>

      <div className="mm-card glass">
        <div className="mm-ipo-moretop">
          <h2>{t('IPO เพิ่มเติม', 'More IPOs')}</h2>
          <span className="mono dim">{ipoRows.length} {t('เหตุการณ์ · ทั่วโลก', 'events · global')}</span>
        </div>
        <div className="mm-scan-scroll" style={{ maxHeight: 'none' }}>
          <table className="mm-ipotbl sortable-tbl">
            <thead><tr>
              <th className="l sortable" onClick={() => sortBy('sym')}>IPO<Arrow k="sym"></Arrow></th>
              <th className="l sortable" onClick={() => sortBy('market')}>{t('ตลาดหลักทรัพย์', 'Exchange')}<Arrow k="market"></Arrow></th>
              <th className="sortable" onClick={() => sortBy('d')}>{t('วันที่เข้า IPO', 'IPO date')}<Arrow k="d"></Arrow></th>
              <th>{t('ช่วงราคา', 'Price range')}</th>
              <th>{t('หุ้นที่เสนอขาย', 'Shares offered')}</th>
              <th className="sortable" onClick={() => sortBy('value')}>{t('มูลค่าตามราคาตลาด', 'Market cap')}<Arrow k="value"></Arrow></th>
              <th className="sortable" onClick={() => sortBy('heat')}>{t('ความสนใจ', 'Interest')}<Arrow k="heat"></Arrow></th>
            </tr></thead>
            <tbody>
              {sortedAll.map(p => {
                const d = parseIso(p.d);
                return (
                  <tr key={p.sym}>
                    <td className="l mm-ipo-co"><SymLogo sym={p.sym} cdnSym={p.sym} className="mm-ipo-logo" optional></SymLogo><span className="mm-ipo-tick mono">{p.sym}</span><span className="mm-ipo-coname">{p.name}</span></td>
                    <td className="l"><span className="mm-ipo-mkt">{p.flag} {p.market}</span></td>
                    <td className="mono">{d.getDate()} {MON(d.getMonth())} {yearLabel(d.getFullYear())}</td>
                    <td className="mono">{p.cur}{p.range}</td>
                    <td className="mono">{p.shares && p.shares !== '0' ? p.shares : '—'}</td>
                    <td className="mono">{p.value && p.value !== '0' ? p.cur + p.value : '—'}</td>
                    <td><div className="mm-heat-bar sm"><i style={{ width: `${p.heat}%` }}></i></div></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ---------- EARNINGS (EarningsHub-style calendar + feed) ----------
const EARN_CAL = [
  { t: 'FCEL', n: 'FuelCell Energy', co: 'US', d: '2026-06-08', sess: 'bmo', col: '#2563eb', est: '-0.18', last: '-0.31', rev: '0.12B', revEst: '0.11B', mcap: '0.4B', when: 'ก่อนเปิด' },
  { t: 'CPB', n: 'Campbell Soup', co: 'US', d: '2026-06-08', sess: 'bmo', col: '#c0271e', est: '0.66', last: '0.75', rev: '2.28B', revEst: '2.31B', mcap: '13B', when: 'ก่อนเปิด' },
  { t: 'UEC', n: 'Uranium Energy', co: 'US', d: '2026-06-09', sess: 'bmo', col: '#15803d', est: '0.04', last: '0.02', rev: '0.08B', revEst: '0.07B', mcap: '3.2B', when: 'ก่อนเปิด' },
  { t: 'SJM', n: 'J.M. Smucker', co: 'US', d: '2026-06-09', sess: 'bmo', col: '#1f2937', est: '2.24', last: '2.66', rev: '2.18B', revEst: '2.20B', mcap: '11B', when: 'ก่อนเปิด' },
  { t: 'SAIL', n: 'SailPoint', co: 'US', d: '2026-06-09', sess: 'bmo', col: '#7c3aed', est: '0.12', last: '0.09', rev: '0.24B', revEst: '0.23B', mcap: '9B', when: 'ก่อนเปิด' },
  { t: 'CASY', n: "Casey's General", co: 'US', d: '2026-06-09', sess: 'amc', col: '#dc2626', est: '3.32', last: '4.37', rev: '4.35B', revEst: '4.57B', mcap: '17B', when: 'หลังปิด' },
  { t: 'CHWY', n: 'Chewy Inc', co: 'US', d: '2026-06-10', sess: 'bmo', col: '#1d4ed8', est: '0.39', last: '0.43', rev: '3.35B', revEst: '3.36B', mcap: '18B', when: 'ก่อนเปิด' },
  { t: 'ORCL', n: 'Oracle Corp', co: 'US', d: '2026-06-10', sess: 'amc', col: '#c0271e', est: '1.89', last: '2.11', rev: '19.09B', revEst: '19.18B', mcap: '620B', when: 'หลังปิด' },
  { t: 'ADBE', n: 'Adobe Inc', co: 'US', d: '2026-06-11', sess: 'amc', col: '#da1f26', est: '5.12', last: '4.48', rev: '5.86B', revEst: '5.91B', mcap: '218B', when: 'หลังปิด' },
  { t: 'LEN', n: 'Lennar Corp', co: 'US', d: '2026-06-11', sess: 'amc', col: '#0e7490', est: '2.14', last: '3.38', rev: '8.20B', revEst: '8.05B', mcap: '38B', when: 'หลังปิด' },
  { t: 'RH', n: 'RH (Restoration)', co: 'US', d: '2026-06-11', sess: 'amc', col: '#1f2937', est: '1.68', last: '1.13', rev: '0.82B', revEst: '0.81B', mcap: '7B', when: 'หลังปิด' },
  { t: 'LOVE', n: 'Lovesac Co', co: 'US', d: '2026-06-11', sess: 'bmo', col: '#0d9488', est: '-0.42', last: '-0.28', rev: '0.16B', revEst: '0.15B', mcap: '0.4B', when: 'ก่อนเปิด' },
  { t: 'NKE', n: 'Nike Inc', co: 'US', d: '2026-06-26', sess: 'amc', col: '#1f2937', est: '0.84', last: '0.99', rev: '11.4B', revEst: '11.6B', mcap: '142B', when: 'หลังปิด' },
  { t: 'FDX', n: 'FedEx Corp', co: 'US', d: '2026-06-23', sess: 'amc', col: '#4d148c', est: '5.42', last: '5.41', rev: '22.1B', revEst: '22.3B', mcap: '60B', when: 'หลังปิด' },
  { t: 'MU', n: 'Micron Tech', co: 'US', d: '2026-06-24', sess: 'amc', col: '#003a70', est: '1.92', last: '0.62', rev: '8.6B', revEst: '8.4B', mcap: '128B', when: 'หลังปิด' },
  { t: 'CPALL', n: 'ซีพี ออลล์', co: 'TH', d: '2026-06-12', sess: 'bmo', col: '#0d7c3f', est: '฿0.82', last: '฿0.76', rev: '฿245B', revEst: '฿242B', mcap: '฿474B', when: 'ก่อนเปิด' },
  { t: 'PTT', n: 'ปตท.', co: 'TH', d: '2026-06-12', sess: 'bmo', col: '#00529b', est: '฿0.94', last: '฿0.88', rev: '฿720B', revEst: '฿712B', mcap: '฿950B', when: 'ก่อนเปิด' },
  { t: '7203', n: 'Toyota Motor', co: 'JP', d: '2026-06-10', sess: 'bmo', col: '#c8102e', est: '¥324', last: '¥298', rev: '¥12.1T', revEst: '¥11.9T', mcap: '¥48T', when: 'ก่อนเปิด' },
  { t: '0700', n: 'Tencent Holdings', co: 'HK', d: '2026-06-11', sess: 'amc', col: '#0ea5e9', est: 'HK$5.2', last: 'HK$5.8', rev: 'HK$162B', revEst: 'HK$158B', mcap: 'HK$3.8T', when: 'หลังปิด' },
  { t: 'ASML', n: 'ASML Holding', co: 'EU', d: '2026-06-09', sess: 'bmo', col: '#1e40af', est: '€4.85', last: '€5.12', rev: '€7.2B', revEst: '€7.1B', mcap: '€364B', when: 'ก่อนเปิด' },
  { t: '005930', n: 'Samsung Elec', co: 'KR', d: '2026-06-12', sess: 'bmo', col: '#1428a0', est: '₩1,420', last: '₩1,380', rev: '₩74T', revEst: '₩73T', mcap: '₩468T', when: 'ก่อนเปิด' },
];

const EARN_COUNTRIES = [
  { id: 'ALL', label: 'ทั่วโลก', en: 'Global', flag: '🌐' },
  { id: 'US', label: 'สหรัฐ', en: 'USA', flag: '🇺🇸' },
  { id: 'TH', label: 'ไทย', en: 'Thailand', flag: '🇹🇭' },
  { id: 'JP', label: 'ญี่ปุ่น', en: 'Japan', flag: '🇯🇵' },
  { id: 'HK', label: 'ฮ่องกง/จีน', en: 'HK/China', flag: '🇭🇰' },
  { id: 'EU', label: 'ยุโรป', en: 'Europe', flag: '🇪🇺' },
  { id: 'KR', label: 'เกาหลี', en: 'Korea', flag: '🇰🇷' },
];

const fmtThaiShort = (d: Date) => `${(_calEn ? EN_DOW[d.getDay()] : ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'][d.getDay()])} ${d.getDate()}`;

// (SymLogo moved to components/core — the same logo-with-initials-fallback is
//  now used by the earnings tiles, the scanner and the dividend screener.)

function EarnTile({ e, onClick, active }: any) {
  return (
    <button className={`mm-etile ${active ? 'on' : ''}`} onClick={onClick} style={{ ['--bc' as any]: e.col }}>
      <SymLogo sym={e.t} cdnSym={e.t} className="mm-etile-logo" style={{ background: e.col }} fallback={e.t.slice(0, 4)}></SymLogo>
      <b className="mono">{e.t}</b>
    </button>
  );
}

function FeedCard({ e }: any) {
  const t = useT();
  // Parse "$1.25", "2.18B", "-0.31" → number; null when absent/non-numeric.
  const num = (v: any) => { const n = parseFloat(String(v ?? '').replace(/[^\d.-]/g, '')); return Number.isFinite(n) ? n : null; };
  const est = num(e.est), last = num(e.last), rev = num(e.rev), revEst = num(e.revEst);
  // Results are "reported" only once an actual EPS exists; upcoming rows have
  // just an estimate, so surprise % / BEAT-MISS are undefined (not NaN).
  const reported = last != null;
  const epsDiff = reported && est != null && est !== 0 ? (last - est) / Math.abs(est) * 100 : null;
  const revDiff = reported && rev != null && revEst != null && revEst !== 0 ? (rev - revEst) / Math.abs(revEst) * 100 : null;
  const beat = epsDiff != null && epsDiff >= 0;
  const pct = (v: number | null) => v == null
    ? <span className="mono dim">—</span>
    : <span className={`mono ${v >= 0 ? 'up' : 'down'}`}>{v >= 0 ? '+' : ''}{v.toFixed(2)}%</span>;
  return (
    <div className="mm-feedcard" style={{ ['--bc' as any]: e.col }}>
      <div className="mm-feed-h">
        <SymLogo sym={e.t} cdnSym={e.t} className="mm-feed-logo" style={{ background: e.col }} fallback={e.t.slice(0, 4)}></SymLogo>
        <div><b>{e.t} Q2 2026 Earnings</b><span className="dim">{e.n}</span></div>
        <span className={`mm-feed-badge ${reported ? (beat ? 'beat' : 'miss') : ''}`}>{reported ? (beat ? 'BEAT' : 'MISS') : t('รอประกาศ', 'UPCOMING')}</span>
      </div>
      <div className="mm-feed-row">
        <span className="mm-feed-k mono">REV</span>
        <span className="mono dim">{e.revEst} <i>est</i></span>
        <span className="mono">{e.rev || '—'}</span>
        {pct(revDiff)}
      </div>
      <div className="mm-feed-row">
        <span className="mm-feed-k mono">EPS</span>
        <span className="mono dim">{e.est} <i>est</i></span>
        <span className="mono">{e.last || '—'}</span>
        {pct(epsDiff)}
      </div>
      <div className="mm-feed-actions">
        <span className="mm-feed-act">▶ Live Call</span>
        <span className="mm-feed-act">✦ AI Summary</span>
        <span className="mm-feed-act">▤ Report</span>
      </div>
    </div>
  );
}

// user/onAuth/onUpgrade arrive only so the two Parlov blocks at the bottom keep
// the paid-member gate they had on the analysis page. Moving a paid feature
// must not quietly turn it into a free one.
export function EarningsPage({ onPick, user, onAuth, onUpgrade }: any) {
  const t = useT();
  const { lang } = useLang();
  _calEn = lang === 'en';
  const [anchor, setAnchor] = useState(() => new Date());
  const [country, setCountry] = useState('ALL');
  const weekStart = useMemo(() => startOfWeek(anchor), [anchor]);
  const weekdays = useMemo(() => Array.from({ length: 5 }, (_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i + 1); return d; }), [weekStart]);
  const todayIso = isoOf(new Date());

  // Real earnings calendar (Finnhub, US) for the window around the displayed week; mock fallback.
  const winFrom = useMemo(() => { const d = new Date(weekStart); d.setDate(d.getDate() - 14); return isoOf(d); }, [weekStart]);
  const winTo = useMemo(() => { const d = new Date(weekStart); d.setDate(d.getDate() + 28); return isoOf(d); }, [weekStart]);
  const earnCal = useEarnings(winFrom, winTo);
  // The feed repeats some tickers on the same day and session (BTLN, CABI, POM,
  // BTM... each listed twice on 28-30 Sep 2026); one tile each.
  const earnRows = useMemo(() => {
    const seen = new Set<string>();
    return earnCal.rows.filter((e: any) => { const k = e.t + '|' + e.d + '|' + e.sess; if (seen.has(k)) return false; seen.add(k); return true; });
  }, [earnCal.rows]);

  // Market-cap filter — Finnhub calendar rows carry no cap, so when a band is
  // selected we pull caps from Yahoo fundamentals for the symbols on screen
  // (this week + recent past for the feed). Until data arrives, show all.
  const [capF, setCapF] = useState('ALL');
  const capSyms = useMemo(() => {
    if (capF === 'ALL') return [] as string[];
    const wk = new Set(weekdays.map(isoOf));
    const inWeek = earnRows.filter((e: any) => wk.has(e.d));
    const past = [...earnRows].filter((e: any) => e.d <= todayIso).sort((a: any, b: any) => b.d.localeCompare(a.d)).slice(0, 40);
    return [...new Set([...inWeek, ...past].map((e: any) => e.t as string))].slice(0, 600);
  }, [capF, earnRows, weekdays, todayIso]);
  const { data: caps } = useFundamentals(capSyms);
  const CAP_BANDS: Record<string, [number, number]> = {
    LARGE: [10e9, Infinity], MID: [2e9, 10e9], SMALL: [300e6, 2e9], MICRO: [0, 300e6],
  };
  const passCap = (e: any) => {
    if (capF === 'ALL') return true;
    if (!Object.keys(caps).length) return true; // caps still loading — don't blank the board
    const m = caps[e.t]?.mcap;
    if (m == null) return false;
    const [lo, hi] = CAP_BANDS[capF];
    return m >= lo && m < hi;
  };

  const byDay = useMemo(() => {
    const m: Record<string, any[]> = {};
    earnRows.filter((e: any) => (country === 'ALL' || e.co === country) && passCap(e)).forEach((e: any) => { (m[e.d] = m[e.d] || []).push(e); });
    return m;
  }, [country, earnRows, capF, caps]);

  const shiftWeek = (n: number) => { const d = new Date(weekStart); d.setDate(d.getDate() + n * 7); setAnchor(d); };
  const goToday = () => setAnchor(new Date());
  const onMonth = (e: any) => { const d = new Date(weekStart); d.setMonth(+e.target.value); setAnchor(d); };

  const feed = useMemo(() => [...earnRows].filter((e: any) => e.d <= todayIso && (country === 'ALL' || e.co === country) && passCap(e)).sort((a: any, b: any) => b.d.localeCompare(a.d)).slice(0, 6), [country, earnRows, todayIso, capF, caps]);
  const weekLabel = `${weekdays[0].getDate()} — ${weekdays[4].getDate()} ${MON(weekdays[4].getMonth())} ${yearLabel(weekdays[4].getFullYear())}`;

  return (
    <div className="mm-page mm-earn-page" data-screen-label="Earnings">
      <div className="mm-earn-layout">
        <div className="mm-earn-main">
          <div className="mm-card glass mm-ipo-cal">
            <div className="mm-ipo-toolbar">
              <div className="mm-ipo-nav">
                <button className="mm-ipo-today" onClick={goToday}>{t('วันนี้', 'Today')}</button>
                <button className="mm-ipo-arrow" onClick={() => shiftWeek(-1)}>‹</button>
                <button className="mm-ipo-arrow" onClick={() => shiftWeek(1)}>›</button>
                <div className="mm-earn-rangewrap"><span className="mm-earn-rangetop mono">{t('ประกาศงบสัปดาห์นี้', 'Earnings this week')}</span><strong className="mm-ipo-range">{weekLabel}</strong></div>
              </div>
              <div className="mm-ipo-pickers">
                <select className="mm-ipo-select" value={weekStart.getMonth()} onChange={onMonth}>
                  {(lang === 'en' ? EN_MONTHS : TH_MONTHS).map((m, i) => <option key={i} value={i}>{m}</option>)}
                </select>
                <select className="mm-ipo-select mm-earn-country" value={country} onChange={e => setCountry(e.target.value)}>
                  {EARN_COUNTRIES.map(c => <option key={c.id} value={c.id}>{c.flag} {lang === 'en' ? c.en : c.label}</option>)}
                </select>
                <select className="mm-ipo-select mm-earn-cap" value={capF} onChange={e => setCapF(e.target.value)}>
                  <option value="ALL">{t('มูลค่าตลาด: ทั้งหมด', 'Market Cap: All')}</option>
                  <option value="LARGE">{t('Large ≥ $10B', 'Large ≥ $10B')}</option>
                  <option value="MID">{t('Mid $2B–10B', 'Mid $2B–10B')}</option>
                  <option value="SMALL">{t('Small $300M–2B', 'Small $300M–2B')}</option>
                  <option value="MICRO">{t('Micro < $300M', 'Micro < $300M')}</option>
                </select>
              </div>
            </div>
            <div className="mm-earn-grid">
              {weekdays.map(d => {
                const iso = isoOf(d);
                const evs = byDay[iso] || [];
                const bmo = evs.filter(e => e.sess === 'bmo');
                const amc = evs.filter(e => e.sess === 'amc');
                const isToday = iso === todayIso;
                return (
                  <div className={`mm-earn-col ${isToday ? 'today' : ''}`} key={iso}>
                    <div className="mm-earn-colh">{isToday && <span className="mm-earn-todaytag">{t('วันนี้', 'Today')}</span>}{fmtThaiShort(d)}</div>
                    {evs.length === 0 && <div className="mm-earn-empty mono">— {earnCal.loading ? t('กำลังโหลด…', 'Loading…') : earnCal.failed ? t('โหลดไม่สำเร็จ', 'Could not load') : t('ไม่มีประกาศงบ', 'No earnings')} —</div>}
                    {bmo.length > 0 && (
                      <div className="mm-earn-sess">
                        <span className="mm-earn-sesst">☀ {t('ก่อนเปิดตลาด', 'Before open')}</span>
                        <div className="mm-earn-tiles">{bmo.map(e => <EarnTile key={`${e.t}|${e.d}`} e={e} onClick={() => onPick && onPick(e.t)}></EarnTile>)}</div>
                      </div>
                    )}
                    {amc.length > 0 && (
                      <div className="mm-earn-sess">
                        <span className="mm-earn-sesst">🌙 {t('หลังปิดตลาด', 'After close')}</span>
                        <div className="mm-earn-tiles">{amc.map(e => <EarnTile key={`${e.t}|${e.d}`} e={e} onClick={() => onPick && onPick(e.t)}></EarnTile>)}</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mm-earn-feed">
          <div className="mm-card glass">
            <div className="mm-feed-top">
              <h2>Earnings Feed</h2>
              <span className="mm-live"><i></i>{t('ประกาศแล้ว', 'Reported')}</span>
            </div>
            <div className="mm-feed-list">
              {feed.map(e => <FeedCard key={`${e.t}|${e.d}`} e={e}></FeedCard>)}
              {!feed.length && <div className="mm-earn-empty mono">— {earnCal.loading ? t('กำลังโหลด…', 'Loading…') : earnCal.failed ? t('โหลดปฏิทินงบไม่สำเร็จ — ลองใหม่อีกครั้ง', 'Could not load the calendar — try again') : t('ยังไม่มีบริษัทที่ประกาศงบในช่วงนี้', 'Nothing reported in this window yet')} —</div>}
            </div>
          </div>
        </div>
      </div>

      {/* Gap behaviour after a report. This belongs under the earnings calendar
          rather than under a single-symbol chart: the calendar says who reports
          and when, and these two say what the open tends to do afterwards. */}
      <MemberGate user={user} onAuth={onAuth} onUpgrade={onUpgrade}
        title={t('สถิติ Gap หลังงบ — เฉพาะสมาชิกแบบเสียเงิน', 'Earnings-gap statistics — paid members only')}
        desc={t('สถิติ gap 31 ปี พร้อมงบการเงินและมุมมองซื้อ/ขายรายตัว สำหรับสมาชิก Gold และ Premium', '31 years of gap statistics with fundamentals and a per-stock read — Gold and Premium members')}>
        {/* Today first, then the 31-year study it is read against. */}
        <CardBoundary name="ParlovDailyTable"><ParlovDailyTable></ParlovDailyTable></CardBoundary>
        <CardBoundary name="ParlovGapBlock"><ParlovGapBlock></ParlovGapBlock></CardBoundary>
      </MemberGate>
    </div>
  );
}

// ---------- DIVIDENDS (TradingView-style high-dividend screener) ----------
const DIV_STOCKS = [
  { sym: 'SPCG', name: 'SPCG Public Co. Ltd.', price: 8.85, chg: -0.56, vol: 339780, relVol: 0.95, mcap: 9.4e9, pe: 26.47, eps: 0.33, epsG: -28.11, yield: 24.27, yieldTTM: 24.27, sector: 'Utilities', rating: 'none' },
  { sym: 'AIMCG', name: 'AIM Commercial Growth Freehold', price: 1.35, chg: 1.50, vol: 115230, relVol: 0.40, mcap: 385.92e6, pe: null, eps: -0.57, epsG: 37.57, yield: 22.56, yieldTTM: 5.22, sector: 'Finance', rating: 'none' },
  { sym: 'HPT', name: 'Home Pottery Public Co. Ltd.', price: 0.13, chg: 8.33, vol: 3560000, relVol: 0.32, mcap: 86.07e6, pe: 4.28, eps: 0.03, epsG: -26.92, yield: 18.58, yieldTTM: 17.15, sector: 'Consumer durables', rating: 'none' },
  { sym: 'CMR', name: 'Chiang Mai Ram Medical Business', price: 1.28, chg: 0.00, vol: 147090, relVol: 0.61, mcap: 5.19e9, pe: null, eps: -0.02, epsG: -124.44, yield: 17.05, yieldTTM: 20.80, sector: 'Health services', rating: 'none' },
  { sym: 'SUPEREIF', name: 'Super Energy Power Plant Infra', price: 5.95, chg: 0.00, vol: 33800, relVol: 0.20, mcap: 3.06e9, pe: 11.46, eps: 0.52, epsG: 158.82, yield: 15.24, yieldTTM: 8.42, sector: 'Finance', rating: 'none' },
  { sym: 'PRI', name: 'Primo Service Solutions PCL', price: 3.36, chg: 0.00, vol: 8400, relVol: 0.09, mcap: 1.08e9, pe: 5.77, eps: 0.58, epsG: -27.22, yield: 14.88, yieldTTM: 5.55, sector: 'Finance', rating: 'none' },
  { sym: 'NYT', name: 'Namyong Terminal Public Co.', price: 3.68, chg: 0.00, vol: 429870, relVol: 0.18, mcap: 4.64e9, pe: 7.04, eps: 0.52, epsG: 49.58, yield: 13.59, yieldTTM: 13.37, sector: 'Transportation', rating: 'none' },
  { sym: 'NETBAY', name: 'Netbay Public Co. Ltd.', price: 9.25, chg: 0.00, vol: 342090, relVol: 0.24, mcap: 1.88e9, pe: null, eps: null, epsG: null, yield: 12.97, yieldTTM: 12.77, sector: 'Technology services', rating: 'neutral' },
  { sym: 'MCS', name: 'M.C.S. Steel Public Co., Ltd.', price: 7.45, chg: 0.00, vol: 121960, relVol: 0.20, mcap: 3.58e9, pe: 4.24, eps: 1.76, epsG: 0.94, yield: 12.75, yieldTTM: 12.67, sector: 'Non-energy minerals', rating: 'none' },
  { sym: 'SMART', name: 'Smart Concrete Public Company', price: 0.54, chg: 1.89, vol: 159500, relVol: 0.25, mcap: 562.45e6, pe: 23.79, eps: 0.02, epsG: -66.91, yield: 12.74, yieldTTM: 12.50, sector: 'Non-energy minerals', rating: 'none' },
  { sym: 'NUT', name: 'Nutrition Profess Public Co.', price: 4.22, chg: 0.96, vol: 300, relVol: 0.01, mcap: 506.4e6, pe: 9.42, eps: 0.45, epsG: null, yield: 12.44, yieldTTM: 12.32, sector: 'Health technology', rating: 'none' },
  { sym: 'PACO', name: 'President Automobile Industries', price: 1.31, chg: -0.76, vol: 133900, relVol: 0.66, mcap: 1.31e9, pe: 10.63, eps: 0.12, epsG: -12.75, yield: 11.36, yieldTTM: 11.45, sector: 'Producer manufacturing', rating: 'none' },
  { sym: 'MII', name: 'MFC Industrial Investment Prop', price: 5.65, chg: 0.00, vol: 4400, relVol: 0.05, mcap: 980.4e6, pe: 7.12, eps: 0.79, epsG: 1.93, yield: 11.14, yieldTTM: 11.71, sector: 'Finance', rating: 'none' },
  { sym: 'SCP', name: 'Southern Concrete Pile Public', price: 8.95, chg: -0.56, vol: 80100, relVol: 0.50, mcap: 2.45e9, pe: 8.20, eps: 1.09, epsG: 2.84, yield: 11.11, yieldTTM: 11.17, sector: 'Non-energy minerals', rating: 'none' },
  { sym: 'APO', name: 'Asian Palm Oil Public Company', price: 2.08, chg: 0.97, vol: 412300, relVol: 0.31, mcap: 1.42e9, pe: 4.21, eps: 0.49, epsG: 47.61, yield: 10.68, yieldTTM: 10.68, sector: 'Process industries', rating: 'none' },
  { sym: 'TISCO', name: 'TISCO Financial Group', price: 98.50, chg: 0.51, vol: 1820000, relVol: 1.12, mcap: 78.8e9, pe: 9.84, eps: 10.01, epsG: 3.42, yield: 8.12, yieldTTM: 8.05, sector: 'Finance', rating: 'buy' },
  { sym: 'KKP', name: 'Kiatnakin Phatra Bank', price: 54.25, chg: -0.46, vol: 2240000, relVol: 0.98, mcap: 45.9e9, pe: 8.12, eps: 6.68, epsG: -4.21, yield: 7.84, yieldTTM: 7.66, sector: 'Finance', rating: 'buy' },
  { sym: 'INTUCH', name: 'Intouch Holdings PCL', price: 88.75, chg: 1.14, vol: 3120000, relVol: 1.04, mcap: 284e9, pe: 14.62, eps: 6.07, epsG: 8.94, yield: 6.42, yieldTTM: 6.28, sector: 'Communications', rating: 'strong-buy' },
  { sym: 'MO', co: 'US', name: 'Altria Group Inc', price: 52.40, chg: 0.34, vol: 6820000, relVol: 1.08, mcap: 89e9, pe: 9.12, eps: 5.75, epsG: 4.21, yield: 7.84, yieldTTM: 7.72, sector: 'Consumer staples', rating: 'buy' },
  { sym: 'VZ', co: 'US', name: 'Verizon Communications', price: 41.85, chg: -0.42, vol: 14200000, relVol: 0.96, mcap: 176e9, pe: 9.64, eps: 4.34, epsG: 2.18, yield: 6.48, yieldTTM: 6.40, sector: 'Communications', rating: 'buy' },
  { sym: 'T', co: 'US', name: 'AT&T Inc', price: 27.30, chg: 0.18, vol: 28400000, relVol: 1.02, mcap: 196e9, pe: 13.21, eps: 2.07, epsG: -1.42, yield: 5.62, yieldTTM: 5.55, sector: 'Communications', rating: 'neutral' },
  { sym: 'XOM', co: 'US', name: 'Exxon Mobil Corp', price: 118.60, chg: 0.74, vol: 13800000, relVol: 0.91, mcap: 472e9, pe: 13.84, eps: 8.57, epsG: 6.32, yield: 3.42, yieldTTM: 3.38, sector: 'Energy minerals', rating: 'strong-buy' },
  { sym: 'SHEL', co: 'UK', name: 'Shell plc', price: 28.45, chg: 0.62, vol: 8200000, relVol: 1.05, mcap: 178e9, pe: 11.42, eps: 2.49, epsG: 5.18, yield: 3.88, yieldTTM: 3.82, sector: 'Energy minerals', rating: 'buy' },
  { sym: 'HSBA', co: 'UK', name: 'HSBC Holdings plc', price: 7.42, chg: -0.27, vol: 22400000, relVol: 0.98, mcap: 138e9, pe: 7.84, eps: 0.95, epsG: 8.42, yield: 7.12, yieldTTM: 6.98, sector: 'Finance', rating: 'buy' },
  { sym: 'ALV', co: 'EU', name: 'Allianz SE', price: 312.40, chg: 0.48, vol: 1240000, relVol: 0.92, mcap: 124e9, pe: 12.16, eps: 25.68, epsG: 7.24, yield: 4.42, yieldTTM: 4.36, sector: 'Finance', rating: 'buy' },
  { sym: 'BAS', co: 'EU', name: 'BASF SE', price: 48.20, chg: -0.36, vol: 3420000, relVol: 1.01, mcap: 43e9, pe: 18.42, eps: 2.62, epsG: -12.42, yield: 6.84, yieldTTM: 6.72, sector: 'Process industries', rating: 'neutral' },
  { sym: '8306', co: 'JP', name: 'Mitsubishi UFJ Financial', price: 1842, chg: 0.84, vol: 18400000, relVol: 1.12, mcap: 22e12, pe: 11.84, eps: 155.6, epsG: 9.42, yield: 3.12, yieldTTM: 3.04, sector: 'Finance', rating: 'buy' },
  { sym: '1928', co: 'HK', name: 'Sands China Ltd', price: 18.46, chg: 1.24, vol: 12400000, relVol: 1.08, mcap: 149e9, pe: 16.42, eps: 1.12, epsG: 18.42, yield: 6.24, yieldTTM: 6.12, sector: 'Consumer services', rating: 'buy' },
] as any[];
const DIV_COUNTRIES = [
  { id: 'ALL', label: 'ทั่วโลก', en: 'Global', flag: '🌐' },
  { id: 'TH', label: 'ไทย (SET)', en: 'Thailand (SET)', flag: '🇹🇭' },
  { id: 'US', label: 'สหรัฐ', en: 'USA', flag: '🇺🇸' },
  { id: 'UK', label: 'อังกฤษ', en: 'UK', flag: '🇬🇧' },
  { id: 'EU', label: 'ยุโรป', en: 'Europe', flag: '🇪🇺' },
  { id: 'JP', label: 'ญี่ปุ่น', en: 'Japan', flag: '🇯🇵' },
  { id: 'HK', label: 'ฮ่องกง', en: 'Hong Kong', flag: '🇭🇰' },
];
const DIV_CUR: Record<string, string> = { TH: 'THB', US: '$', UK: '£', EU: '€', JP: '¥', HK: 'HK$' };
const DIV_MKT: Record<string, string> = { TH: 'ตลาดหุ้นไทย (SET)', US: 'ตลาดหุ้นสหรัฐ', UK: 'ตลาดหุ้นลอนดอน', EU: 'ตลาดหุ้นยุโรป', JP: 'ตลาดหุ้นญี่ปุ่น', HK: 'ตลาดหุ้นฮ่องกง' };
const DIV_MKT_EN: Record<string, string> = { TH: 'Thai stocks (SET)', US: 'US stocks', UK: 'London stocks', EU: 'European stocks', JP: 'Japanese stocks', HK: 'Hong Kong stocks' };

const fmtTHB = (v: number, d = 2) => v.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
const RATING_TH: Record<string, string> = { 'strong-buy': 'มีแรงซื้อรุนแรง', buy: 'มีแรงซื้อ', neutral: 'เป็นกลาง', sell: 'มีแรงขาย', none: '— ไม่มีเรตติ้ง' };
const RATING_EN: Record<string, string> = { 'strong-buy': 'Strong buy', buy: 'Buy', neutral: 'Neutral', sell: 'Sell', none: '— No rating' };

export function DividendsPage({ onPick }: any) {
  const t = useT();
  const { lang } = useLang();
  const RATING = (k: string) => (lang === 'en' ? RATING_EN[k] : RATING_TH[k]);
  const [view, setView] = useState('screener');
  const [country, setCountry] = useState('ALL');
  const [sector, setSector] = useState('ทุกหมวด');
  const [sortK, setSortK] = useState('yield');
  const [dir, setDir] = useState(-1);
  const curOf = (a: any) => DIV_CUR[a.co || 'TH'] || 'THB';

  // Real dividend (XD) calendar (FMP) for ~7 weeks around today; mock fallback.
  const divFrom = useMemo(() => { const d = new Date(); d.setDate(d.getDate() - 7); return isoOf(d); }, []);
  const divTo = useMemo(() => { const d = new Date(); d.setDate(d.getDate() + 45); return isoOf(d); }, []);
  const divCal = useDividends(divFrom, divTo, DIVIDENDS_LIST as any[]).rows;

  const cols = [
    { k: 'yield', t: 'Div yield % (indicated)', s: (a: any) => a.yield, r: (a: any) => <b className="mono up">{a.yield.toFixed(2)}%</b> },
    { k: 'price', t: t('ราคา', 'Price'), s: (a: any) => a.price, r: (a: any) => <span className="mono">{fmtTHB(a.price)} <i className="mm-unit">{curOf(a)}</i></span> },
    { k: 'chg', t: 'Chg %', s: (a: any) => a.chg, r: (a: any) => <span className={`mono ${a.chg >= 0 ? 'up' : 'down'}`}>{a.chg >= 0 ? '+' : ''}{a.chg.toFixed(2)}%</span> },
    { k: 'vol', t: 'Vol', s: (a: any) => a.vol, r: (a: any) => <span className="mono dim">{fmtBig(a.vol)}</span> },
    { k: 'relVol', t: 'Rel vol', s: (a: any) => a.relVol, r: (a: any) => <span className="mono dim">{a.relVol.toFixed(2)}</span> },
    // curOf, not a hardcoded THB. The price column above already reads the
    // asset's own currency; market cap and EPS did not, so NVDA's cap was
    // labelled THB on a screener that lists US, UK, EU, JP and HK names
    // alongside the Thai ones.
    { k: 'mcap', t: 'Mkt cap', s: (a: any) => a.mcap, r: (a: any) => <span className="mono">{fmtBig(a.mcap)} <i className="mm-unit">{curOf(a)}</i></span> },
    { k: 'pe', t: 'P/E', s: (a: any) => a.pe ?? -1, r: (a: any) => <span className="mono">{a.pe == null ? '—' : a.pe.toFixed(2)}</span> },
    { k: 'eps', t: 'EPS dil TTM', s: (a: any) => a.eps ?? -1e9, r: (a: any) => <span className="mono">{a.eps == null ? '—' : a.eps.toFixed(2)}{a.eps != null && <i className="mm-unit"> {curOf(a)}</i>}</span> },
    { k: 'epsG', t: 'EPS dil growth TTM YoY', s: (a: any) => a.epsG ?? -1e9, r: (a: any) => a.epsG == null ? <span className="dim">—</span> : <span className={`mono ${a.epsG >= 0 ? 'up' : 'down'}`}>{a.epsG >= 0 ? '+' : ''}{a.epsG.toFixed(2)}%</span> },
    { k: 'yieldTTM', t: 'Div yield % TTM', s: (a: any) => a.yieldTTM, r: (a: any) => <span className="mono">{a.yieldTTM.toFixed(2)}%</span> },
    { k: 'sector', t: 'Sector', align: 'l', s: (a: any) => a.sector, r: (a: any) => <span className="dim mm-sec">{a.sector}</span> },
    { k: 'rating', t: 'Analyst rating', align: 'l', s: (a: any) => a.rating, r: (a: any) => <span className={`mm-rating ${a.rating === 'none' ? 'neutral' : a.rating}`}>{a.rating === 'none' ? '—' : a.rating === 'strong-buy' ? '⤊' : a.rating === 'buy' ? '↑' : a.rating === 'sell' ? '↓' : '→'} {RATING(a.rating)}</span> },
  ];

  // Real fundamentals overlay (Yahoo, keyless). One batch call for every
  // mappable symbol; Yahoo-less fields (sector/rating/relVol/epsG) stay curated.
  const symMap = useMemo(() => {
    const m: Record<string, string> = {};
    for (const a of DIV_STOCKS) { const y = divYahoo(a.sym, (a as any).co); if (y) m[a.sym] = y; }
    return m;
  }, []);
  const ySyms = useMemo(() => Array.from(new Set(Object.values(symMap))), [symMap]);
  const { data: fund, live: fundLive } = useFundamentals(ySyms);

  const enriched = useMemo(() => DIV_STOCKS.map(a => {
    const f = fund[symMap[a.sym]];
    if (!f) return a;
    return {
      ...a,
      price: f.price ?? a.price,
      chg: f.chg ?? a.chg,
      vol: f.vol ?? a.vol,
      mcap: f.mcap ?? a.mcap,
      pe: f.pe ?? a.pe,
      eps: f.eps ?? a.eps,
      yield: f.yield ?? a.yield,
      _live: true,
    };
  }), [fund, symMap]);

  const rows = useMemo(() => {
    let r = enriched.filter(a => country === 'ALL' || ((a as any).co || 'TH') === country);
    if (sector !== 'ทุกหมวด') r = r.filter(a => a.sector === sector);
    const col = cols.find(c => c.k === sortK);
    const sf = col ? col.s : ((a: any) => a.yield);
    return [...r].sort((a, b) => { const x = sf(a), y = sf(b); return (typeof x === 'string' ? (x < y ? -1 : x > y ? 1 : 0) : x - y) * dir; });
  }, [enriched, country, sector, sortK, dir]);

  const sectorsForCountry = useMemo(() => {
    const base = DIV_STOCKS.filter(a => country === 'ALL' || (a.co || 'TH') === country);
    return ['ทุกหมวด', ...Array.from(new Set(base.map(a => a.sector)))];
  }, [country]);
  const mktLabel = country === 'ALL' ? t('ตลาดหุ้นทั่วโลก', 'Global stocks') : (lang === 'en' ? DIV_MKT_EN[country] : DIV_MKT[country]);

  const sortBy = (k: string) => { if (sortK === k) setDir(-dir); else { setSortK(k); setDir(-1); } };

  return (
    <div className="mm-page" data-screen-label="Dividends">
      <div className="mm-card glass mm-scan-card">
        <div className="mm-scan-top">
          <div className="mm-scan-title">
            <h2>{t('หุ้นปันผลสูง', 'High-dividend stocks')}</h2>
            <span className="mono dim">{rows.length} {t('หลักทรัพย์', 'securities')} · {mktLabel} · <span title={fundLive ? t('ราคา/มาร์เก็ตแคป/P·E/EPS/yield ดึงสดจาก Yahoo Finance', 'Price/mkt-cap/P·E/EPS/yield live from Yahoo Finance') : t('กำลังโหลด/ไม่มีข้อมูลสด — ใช้ค่าตั้งต้น', 'Loading / no live data — using defaults')}>{fundLive ? t('● สด', '● live') : t('○ จำลอง', '○ simulated')}</span></span>
          </div>
          <div className="mm-div-tools">
            <select className="mm-ipo-select mm-earn-country" value={country} onChange={e => { setCountry(e.target.value); setSector('ทุกหมวด'); }}>
              {DIV_COUNTRIES.map(c => <option key={c.id} value={c.id}>{c.flag} {lang === 'en' ? c.en : c.label}</option>)}
            </select>
            <div className="mm-scan-views">
              <button className={`mm-vtab ${view === 'screener' ? 'on' : ''}`} onClick={() => setView('screener')}>{t('ตัวคัดกรอง', 'Screener')}</button>
              <button className={`mm-vtab ${view === 'calendar' ? 'on' : ''}`} onClick={() => setView('calendar')}>{t('ปฏิทิน XD', 'XD calendar')}</button>
            </div>
          </div>
        </div>

        {view === 'screener' ? (
          <React.Fragment>
            <div className="mm-scan-chips">
              {sectorsForCountry.map(s => (
                <button key={s} className={`mm-fchip ${sector === s ? 'on' : ''}`} onClick={() => setSector(s)}>{s === 'ทุกหมวด' ? t('ทุกหมวด', 'All sectors') : s}</button>
              ))}
            </div>
            <div className="mm-scan-scroll">
              <table className="mm-scantbl">
                <thead>
                  <tr>
                    <th className="sticky-l">Symbol <i className="mm-symcount mono">{rows.length}</i></th>
                    {cols.map(c => (
                      <th key={c.k} className={`${c.align === 'l' ? 'l' : 'r'} sortable ${sortK === c.k ? 'on' : ''}`} onClick={() => sortBy(c.k)}>
                        {c.t}{sortK === c.k ? <i className="sarr">{dir === -1 ? ' ↓' : ' ↑'}</i> : ''}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((a, i) => (
                    <tr key={a.sym} onClick={() => onPick(a.sym)} className="mm-fadein-row" style={{ animationDelay: `${i * 22}ms` }}>
                      <td className="sticky-l mm-scan-sym">
                        <SymLogo sym={a.sym} cdnSym={symMap[a.sym]}
                          style={{ background: 'hsl(160 50% 20%)', color: 'var(--up)', borderColor: 'hsl(160 50% 38%)' }}></SymLogo>
                        <span className="mm-scan-symtxt"><b className="mono">{a.sym}<i className="mm-dbadge">D</i></b><em>{a.name}</em></span>
                      </td>
                      {cols.map(c => <td key={c.k} className={c.align === 'l' ? 'l' : 'r'}>{c.r(a)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mm-footnote dim">{t('เรียงตาม Div yield (indicated) จากมากไปน้อย · คลิกหัวคอลัมน์เพื่อจัดเรียง · ป้าย', 'Sorted by Div yield (indicated), high to low · click a header to sort · badge')} <b style={{ color: 'var(--up)' }}>D</b> = {t('จ่ายปันผลสม่ำเสมอ', 'consistent payer')}</p>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <table className="mm-table hoverable">
              <thead><tr><th>{t('หลักทรัพย์', 'Security')}</th><th>{t('ประเทศ', 'Country')}</th><th>{t('วันขึ้น XD', 'Ex-date')}</th><th>{t('วันจ่าย', 'Pay date')}</th><th>{t('เงินปันผล / หุ้น', 'Div / share')}</th><th>Yield</th><th>{t('ความถี่', 'Frequency')}</th></tr></thead>
              <tbody>
                {/* d.co from the ticker's exchange (server); unknown shows only under Global */}
                {divCal.filter((d: any) => country === 'ALL' || d.co === country).map((d: any, i: number) => (
                  <tr key={d.sym + d.exDate} onClick={() => onPick(d.sym)} className="mm-fadein-row" style={{ animationDelay: `${i * 50}ms` }}>
                    <td><b className="mono">{d.sym}</b><span className="dim mm-cellname">{d.name}</span></td>
                    <td><span className="mm-ipo-mkt">{d.flag || '🌐'}</span></td>
                    <td><span className="mm-chip">{d.exDate}</span></td>
                    <td className="dim">{d.payDate}</td>
                    <td className="mono">{d.amount}</td>
                    <td><div className="mm-scorebar yield"><i style={{ width: `${Math.min(100, d.yield * 14)}%` }}></i><span className="mono up">{d.yield.toFixed(2)}%</span></div></td>
                    <td className="dim">{d.freq}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mm-footnote dim">{t('เคล็ดลับ: ซื้อก่อนวันขึ้น XD อย่างน้อย 1 วันทำการ จึงจะมีสิทธิรับเงินปันผลรอบนั้น', 'Tip: buy at least 1 business day before the ex-date to be eligible for that dividend')}</p>
          </React.Fragment>
        )}
      </div>
    </div>
  );
}
